import { environment } from 'environments/environment';
import { FeatureFlagContext } from '../services/organization/feature-flags.service';

export class Routes
{
    public static AccountingV2Module = {
        Ledger: {
            GenerateLedger: (organizationId: number) => {
                return `/accounting/v2/organizations/${organizationId}/general-ledger/generate`;
            },
            DeleteBatch: (organizationId: number, batchId: string) => {
                return `/accounting/v2/organizations/${organizationId}/general-ledger/${batchId}`;
            },
        },
        Accounts: (organizationId: number, accountType: 'assets' | 'liabilities' | 'equities') => {
            return `/accounting/v2/organizations/${organizationId}/accounts/${accountType}`;
        },
        Account: (organizationId: number, accountType: 'assets' | 'liabilities' | 'equities', accountId: number) => {
            return Routes.AccountingV2Module.Accounts(organizationId, accountType) + '/' + accountId;
        },
        Categories: (organizationId: number) => {
            return `/accounting/v2/organizations/${organizationId}/categories`;
        },
        Category: (organizationId: number, categoryId: number) => {
            return `/accounting/v2/organizations/${organizationId}/categories/${categoryId}`;
        },
        DeleteCategory: (organizationId: number, categoryId: number, replaceWith?: number) => {
            return Routes.AccountingV2Module.Category(organizationId, categoryId) + (replaceWith
                    ? `?replaceWith=${replaceWith}`
                    : '');
        },
        ReorderCategories: (organizationId: number) => {
            return `/accounting/v2/organizations/${organizationId}/categories/reorder`;
        },
        ReorderCategoriesTree: (organizationId: number) => {
            return `/accounting/v2/organizations/${organizationId}/categories/reorder-tree`;
        },
        BulkDeleteAccounts: (organizationId: number, accountType: 'assets' | 'liabilities' | 'equities') => {
            return `/accounting/v2/organizations/${organizationId}/accounts/${accountType}/bulk`;
        },
        ReorderAccounts: (organizationId: number, accountType: 'assets' | 'liabilities' | 'equities') => {
            return `/accounting/v2/organizations/${organizationId}/accounts/${accountType}/reorder`;
        },
        BulkSetParentCategory: (organizationId: number) => {
            return `/accounting/v2/organizations/${organizationId}/categories/bulk-set-parent`;
        },
        BulkDeleteCategories: (organizationId: number) => {
            return `/accounting/v2/organizations/${organizationId}/categories/bulk`;
        },
    };

    public static ReportingV2Module = {
        OrganizationReport: (organizationId: number, report: string, format: string) => {
            return `/reporting/v2/organizations/${organizationId}/reports/${report}/${format}`;
        },
    };

    public static AIReportsModule = {
        FavoriteReports: (organizationId: number): string => {
            return `/ai-reports/organizations/${organizationId}/favorite-reports`;
        },
        FavoriteReport: (organizationId: number, reportKey: string): string => {
            return `/ai-reports/organizations/${organizationId}/favorite-reports/${reportKey}`;
        },
        Conversation: (organizationId: number): string => {
            return `/ai-reports/organizations/${organizationId}/conversation`;
        },
        Chat: (organizationId: number): string => {
            return `/ai-reports/organizations/${organizationId}/chat`;
        },
        Generate: (organizationId: number): string => {
            return `/ai-reports/organizations/${organizationId}/generate`;
        },
    };

    public static AiTelemetryModule = {
        RecordFeedback: (eventId: number): string => {
            return `/ai-telemetry/events/${eventId}/feedback`;
        },
        RecordChipFeedback: (eventId: number): string => {
            return `/ai-telemetry/events/${eventId}/chip-feedback`;
        },
    };

    public static AssistantPocModule = {
        Chat: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/chat`;
        },
        ChatStream: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/chat-stream`;
        },
        AnswerQuestions: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/answer-questions`;
        },
        PendingProposals: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/proposals/pending`;
        },
        ApproveProposal: (organizationId: number, proposalId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/proposals/${proposalId}/approve`;
        },
        RejectProposal: (organizationId: number, proposalId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/proposals/${proposalId}/reject`;
        },
        CommitAction: (organizationId: number, proposalId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/proposals/${proposalId}/commit-action`;
        },
        RefineProposal: (organizationId: number, proposalId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/proposals/${proposalId}/refine`;
        },
        CancelStream: (organizationId: number, messageId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/messages/${messageId}/cancel-stream`;
        },
        ToolCards: (organizationId: number, conversationId: number, messageId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/conversations/${conversationId}/messages/${messageId}/tool-cards`;
        },
        // SD-6742 — chat history. List/load/resume/rename/archive of past
        // assistant conversations. All scoped to user + org + context_type
        // server-side; foreign ids 404.
        Conversations: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/conversations`;
        },
        Conversation: (organizationId: number, conversationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/conversations/${conversationId}`;
        },
        ArchiveConversation: (organizationId: number, conversationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/conversations/${conversationId}/archive`;
        },
        UnarchiveConversation: (organizationId: number, conversationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/conversations/${conversationId}/unarchive`;
        },
        Lessons: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/lessons`;
        },
        Lesson: (organizationId: number, lessonId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/lessons/${lessonId}`;
        },
        LessonsSimilar: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/lessons/similar`;
        },
        Catalog: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/catalog`;
        },
        CatalogItem: (organizationId: number, id: number): string => {
            return `/poc/assistant/organizations/${organizationId}/catalog/${id}`;
        },
        CatalogApproveView: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/catalog/approve-view`;
        },
        KnowledgeDrafts: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/knowledge/drafts`;
        },
        KnowledgeDraftItem: (organizationId: number, id: number): string => {
            return `/poc/assistant/organizations/${organizationId}/knowledge/drafts/${id}`;
        },
        KnowledgeDraftApprove: (organizationId: number, id: number): string => {
            return `/poc/assistant/organizations/${organizationId}/knowledge/drafts/${id}/approve`;
        },
        KnowledgeDraftReject: (organizationId: number, id: number): string => {
            return `/poc/assistant/organizations/${organizationId}/knowledge/drafts/${id}/reject`;
        },
        KnowledgeVerifiedQueryApprove: (organizationId: number, id: number): string => {
            return `/poc/assistant/organizations/${organizationId}/knowledge/verified-queries/${id}/approve`;
        },
        KnowledgeVerifiedQueryReject: (organizationId: number, id: number): string => {
            return `/poc/assistant/organizations/${organizationId}/knowledge/verified-queries/${id}/reject`;
        },
        AiActivitySummary: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/ai-activity/summary`;
        },
        AiActivityOrganizations: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/ai-activity/organizations`;
        },
        AiActivityUsers: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/ai-activity/users`;
        },
        AiActivityInteractions: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/ai-activity/interactions`;
        },
        AiActivityInteraction: (organizationId: number, id: number): string => {
            return `/poc/assistant/organizations/${organizationId}/ai-activity/interactions/${id}`;
        },
        AiActivityNeedsReview: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/ai-activity/needs-review`;
        },
        AiActivityPromoteToLesson: (organizationId: number, id: number): string => {
            return `/poc/assistant/organizations/${organizationId}/ai-activity/interactions/${id}/promote-to-lesson`;
        },
        AiActivityExportCsv: (organizationId: number): string => {
            return `/poc/assistant/organizations/${organizationId}/ai-activity/export.csv`;
        },
    };

    public static OrganizationsV2Module = {
        Organizations: `/api/v2/organizations`,
        Organization: (organizationId: number) => {
            return Routes.OrganizationsV2Module.Organizations + `/${organizationId}`;
        },
    };

    public static PMCV2Module = {
        PropertyManagementCompanies: `/api/v2/pmcs`,
        PropertyManagementCompaniesPendingReview: () => {
            return Routes.PMCV2Module.PropertyManagementCompanies + `/pending`;
        },
        PropertyManagementCompaniesAvailableUsers: () => {
            return Routes.PMCV2Module.PropertyManagementCompanies + `/available-users`;
        },
        PropertyManagementCompaniesAvailableOrganizations: () => {
            return Routes.PMCV2Module.PropertyManagementCompanies + `/organizations`;
        },
        PropertyManagementCompaniesFromUserPreview: (userId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompaniesAvailableUsers() + `/${userId}`;
        },
        ReviewPmc: (pmcId: number, action: string) => {
            return Routes.PMCV2Module.PropertyManagementCompaniesPendingReview() + `/${pmcId}/${action}`;
        },

        PropertyManagementCompany: (pmcId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompanies + `/${pmcId}`;
        },
        RestorePropertyManagementCompany: (pmcId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompany(pmcId) + `/restore`;
        },

        PmcBilling: (pmcId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompany(pmcId) + `/billing`;
        },

        PmcPaymentMethods: (pmcId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompany(pmcId) + `/payment-methods`;
        },

        PmcRates: (pmcId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompany(pmcId) + `/rates`;
        },

        PmcOrgRates: (pmcId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompany(pmcId) + `/org-rates`;
        },
        PmcOrgRatesSyncAll: (pmcId: number) => {
            return Routes.PMCV2Module.PmcOrgRates(pmcId) + `/sync-all`;
        },
        PmcOrgRatesSync: (pmcId: number, orgId: number) => {
            return Routes.PMCV2Module.PmcOrgRates(pmcId) + `/${orgId}/sync`;
        },
        PmcOrgRatesUpdate: (pmcId: number, orgId: number) => {
            return Routes.PMCV2Module.PmcOrgRates(pmcId) + `/${orgId}`;
        },

        PmcOrganizations: (pmcId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompany(pmcId) + `/organizations`;
        },
        PmcOrganizationList: (pmcId: number) => {
            return Routes.PMCV2Module.PmcOrganizations(pmcId) + `/list`;
        },
        PmcOrganization: (pmcId: number, orgId: number) => {
            return Routes.PMCV2Module.PmcOrganizations(pmcId) + `/${orgId}`;
        },
        PmcOrganizationCopyContent: (pmcId: number, orgId: number) => {
            return Routes.PMCV2Module.PmcOrganization(pmcId, orgId) + `/copy-content`;
        },

        PmcTeam: (pmcId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompany(pmcId) + `/team`;
        },
        PmcTeamUser: (pmcId: number, userId: number) => {
            return Routes.PMCV2Module.PmcTeam(pmcId) + `/${userId}`;
        },

        Violations: (pmcId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompany(pmcId) + `/violations`;
        },
        Violation: (pmcId: number, violationId: number) => {
            return Routes.PMCV2Module.Violations(pmcId) + `/${violationId}`;
        },
        ViolationStatuses: (pmcId: number) => {
            return Routes.PMCV2Module.Violations(pmcId) + `/statuses`;
        },
        ViolationsCount: (pmcId: number) => {
            return Routes.PMCV2Module.Violations(pmcId) + `/count`;
        },
        Forms: (pmcId: number) => {
            return Routes.PMCV2Module.PropertyManagementCompany(pmcId) + `/forms`;
        },
        FormsSubmissionStatuses: (pmcId: number) => {
            return Routes.PMCV2Module.Forms(pmcId) + `/submission-statuses`;
        },
        FormSubmissions: (pmcId: number) => {
            return Routes.PMCV2Module.Forms(pmcId) + `/submissions`;
        },
        FormSubmission: (pmcId: number, submissionId: number) => {
            return Routes.PMCV2Module.FormSubmissions(pmcId) + `/${submissionId}`;
        },
        FormsCount: (pmcId: number) => {
            return Routes.PMCV2Module.Forms(pmcId) + `/count`;
        },
    };

    public static UnitsV2Module = {
        OrganizationUnits: (organizationId: number) => {
            return `/api/v2/organizations/${organizationId}/units`;
        },
        GetUnitsByIds: (organizationId: number) => {
            return `/api/v2/organizations/${organizationId}/units/by-ids`;
        },
    };

    public static CoreV2Module = {
        ActivityLogs: () => {
            return `/core/v2/activity-logs`;
        },
        OrganizationActivityLogs: (organizationId: number) => {
            return `/core/v2/activity-logs/organization/${organizationId}`;
        },

        AvailableFeatureFlags: (context: FeatureFlagContext) => {
            return `/core/v2/feature-flags/${context}/available`;
        },
        FeatureFlags: (context: FeatureFlagContext, contextId: number) => {
            return `/core/v2/feature-flags/${context}/${contextId}`;
        },

        CommunicationActivityAttachment: function (
            orgId: number,
            communicationActivityId: number,
            fileAttachmentId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/communications/' +
                communicationActivityId +
                '/fileAttachments/' +
                fileAttachmentId;
        },
    };

    /**
     * Generic per-user-per-org preference storage (SD-6488 core module).
     * The org is part of the URI (never the body) so it can't mismatch the
     * authorized scope. Core registers these with apiPrefix '' — no `/api`.
     */
    public static UserPreferences = {
        Collection: (organizationId: number) => {
            return `/v1/organizations/${organizationId}/user-preferences`;
        },
        Item: (organizationId: number, key: string) => {
            return `/v1/organizations/${organizationId}/user-preferences/${key}`;
        },
    };

    public static StripeModule = {
        StripeModule: `/stripe/v2`,
        AppConfig: `/api/config`,
        Coupons(context: 'organizations' | 'pmcs', contextId: number): string {
            return `${Routes.StripeModule.StripeModule}/${context}/${contextId}/coupons`;
        },
        Products(context: 'organizations' | 'pmcs', contextId: number): string {
            return `${Routes.StripeModule.StripeModule}/${context}/${contextId}/products`;
        },
        Invoices(context: 'organizations'|'pmcs', contextId: number) {
            return `${Routes.StripeModule.StripeModule}/${context}/${contextId}/invoices`;
        },
        PayOpenInvoices(context: string, contextId: number) {
            return `${Routes.StripeModule.StripeModule}/${context}/${contextId}/invoices/pay`;
        },
        Invoice(context: string, contextId: number, invoiceId: string) {
            return `${Routes.StripeModule.StripeModule}/${context}/${contextId}/invoices/${invoiceId}`;
        },
        FinalizeInvoice(context: string, contextId: number, invoiceId: string) {
            return `${Routes.StripeModule.StripeModule}/${context}/${contextId}/invoices/${invoiceId}/finalize`;
        },
        Subscriptions(context: string, contextId: number): string {
            return `${Routes.StripeModule.StripeModule}/${context}/${contextId}/subscriptions`;
        },
        Subscription(context: string, contextId: number, subscriptionId: string): string {
            return Routes.StripeModule.Subscriptions(context, contextId) + `/${subscriptionId}`;
        },
        SubscriptionCoupons(context: string, organizationId: number, subscriptionId: string): string {
            return Routes.StripeModule.Subscription(context, organizationId, subscriptionId) + `/coupons`;
        },
        ConvertSubscriptionToAnnual(context: string, organizationId: number, subscriptionId: string): string {
            return Routes.StripeModule.Subscription(context, organizationId, subscriptionId) + `/convert-to-annual`;
        },
        CustomerBalance: (context: string, contextId: number): string => {
            return `${Routes.StripeModule.StripeModule}/${context}/${contextId}/customer-balance`;
        },
        CreateSetupIntent: (context: 'users' | 'pmcs' | 'organizations', contextId: number): string => {
            return `${Routes.StripeModule.StripeModule}/${context}/${contextId}/create-setup-intent`;
        },
        CreateConnectAccount: (organizationId: number): string => {
            return `${Routes.StripeModule.StripeModule}/organizations/${organizationId}/connect`;
        },
        CreateConnectAccountSession: (organizationId: number, connectAccountId: number): string => {
            return `${Routes.StripeModule.StripeModule}/organizations/${organizationId}/connect/${connectAccountId}/session`;
        },
        ConnectAccountStatus: (organizationId: number): string => {
            return `${Routes.StripeModule.StripeModule}/organizations/${organizationId}/connect/status`;
        },
        ComponentSession: (organizationId: number): string => {
            return `${Routes.StripeModule.StripeModule}/organizations/${organizationId}/connect/component-session`;
        },
    };

    public static TreasuryModule = {
        Base: '/treasury/v1',
        Balance: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/balance`;
        },
        Transactions: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/transactions`;
        },
        Statements: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/statements`;
        },
        OutboundTransfers: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/outbound-transfers`;
        },
        InboundTransfers: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/inbound-transfers`;
        },
        PendingTransfers: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/pending-transfers`;
        },
        PendingTransferDismiss: (organizationId: number, type: string, transferId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/pending-transfers/${type}/${transferId}/dismiss`;
        },
        PendingTransferRetry: (organizationId: number, type: string, transferId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/pending-transfers/${type}/${transferId}/retry`;
        },
        RecentActivity: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/recent-activity`;
        },
        Statistics: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/statistics`;
        },
        FinancialConnectionsSessions: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/financial-connections/sessions`;
        },
        FinancialConnectionsAccounts: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/financial-connections/accounts`;
        },
        FinancialConnectionsAccountDefaultName: (organizationId: number, fcAccountId: string): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/financial-connections/accounts/${fcAccountId}/default-name`;
        },
        FinancialConnectionsAccountSyncBalance: (organizationId: number, fcAccountId: string): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/financial-connections/accounts/${fcAccountId}/sync-balance`;
        },
        FinancialConnectionsLinkExternalBalance: (organizationId: number, ubaId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/financial-connections/external-accounts/${ubaId}/link-balance`;
        },
        RefundablePayments: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/refundable-payments`;
        },
        Refunds: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/refunds`;
        },
        VendorBankAccounts: (organizationId: number, vendorId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/vendors/${vendorId}/bank-accounts`;
        },
        VendorBankAccount: (organizationId: number, vendorId: number, bankAccountId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/vendors/${vendorId}/bank-accounts/${bankAccountId}`;
        },
        OutboundPayments: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/outbound-payments`;
        },
        OutboundPayment: (organizationId: number, paymentId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/outbound-payments/${paymentId}`;
        },
        Transaction: (organizationId: number, transactionId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/transactions/${transactionId}`;
        },
        Cardholders: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cardholders`;
        },
        Cardholder: (organizationId: number, cardholderId: string): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cardholders/${cardholderId}`;
        },
        CardholderReactivate: (organizationId: number, cardholderId: string): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cardholders/${cardholderId}/reactivate`;
        },
        Cards: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards`;
        },
        Card: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/${cardId}`;
        },
        CardTransactions: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/${cardId}/transactions`;
        },
        CardSpendingControls: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/${cardId}/spending-controls`;
        },
        CardEphemeralKey: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/${cardId}/ephemeral-key`;
        },
        CardDetailsPdf: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/${cardId}/pdf`;
        },
        CardTransactionsPdf: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/${cardId}/transactions/pdf`;
        },
        CardTestTransaction: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/${cardId}/test-transaction`;
        },
        FraudNotificationPreferences: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/fraud-notification-preferences`;
        },
        SecondUnderwritingQueue: (): string => {
            return `${Routes.TreasuryModule.Base}/admin/second-underwriting`;
        },
        SecondUnderwritingAction: (organizationId: number, action: string): string => {
            return `${Routes.TreasuryModule.Base}/admin/second-underwriting/${organizationId}/${action}`;
        },
        UnderwritingStatus: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/underwriting-status`;
        },
        PaymentReadiness: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/payment-readiness`;
        },
        RequestExpeditedApproval: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/request-expedited-approval`;
        },
        AllCardTransactions: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/card-transactions`;
        },
        AllCardTransactionsPdf: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/card-transactions/pdf`;
        },
        CardDisputes: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/${cardId}/disputes`;
        },
        Dispute: (organizationId: number, disputeId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/disputes/${disputeId}`;
        },
        MyCardDisputes: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/my-cards/${cardId}/disputes`;
        },
        // TEMPORARY — Test mode endpoints (delete when testing complete)
        TestMode: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/test-mode`;
        },
        SimulateInboundTransfer: (organizationId: number, transferId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/inbound-transfers/${transferId}/simulate`;
        },
        SimulateOutboundTransfer: (organizationId: number, transferId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/outbound-transfers/${transferId}/simulate`;
        },
        SimulateOutboundPayment: (organizationId: number, paymentId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/outbound-payments/${paymentId}/simulate`;
        },
        SimulatePayment: (organizationId: number, paymentAttemptId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/payments/${paymentAttemptId}/simulate`;
        },
        CardsBulkActivate: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/bulk/activate`;
        },
        CardsBulkDeactivate: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/bulk/deactivate`;
        },
        CardsBulkCancel: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/bulk/cancel`;
        },
        CardsBulkDelete: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/bulk/delete`;
        },
        CardsBulkRestore: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/bulk/restore`;
        },
        CardRestore: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/${cardId}/restore`;
        },
        CardResendActivation: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/cards/${cardId}/resend-activation`;
        },
        CardActivation: (token: string): string => {
            return `${Routes.TreasuryModule.Base}/activate-card/${token}`;
        },
        CardholderCards: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder`;
        },
        CardholderSession: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder/session`;
        },
        CardholderEphemeralKey: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder/${cardId}/ephemeral-key`;
        },
        CardholderResendActivation: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder/${cardId}/resend-activation`;
        },
        CardholderCardDetailsPdf: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder/${cardId}/pdf`;
        },
        CardholderCardSpending: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder/${cardId}/spending`;
        },
        CardholderTransactionsPdf: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder/${cardId}/transactions/pdf`;
        },
        CardholderAllTransactionsPdf: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder/transactions/pdf`;
        },
        CardholderTransactions: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder/transactions`;
        },
        CardholderDisputes: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder/disputes`;
        },
        CardholderUpdateStatus: (organizationId: number, cardId: number): string => {
            return `${Routes.TreasuryModule.Base}/organizations/${organizationId}/spend-cards/cardholder/${cardId}/status`;
        },
        AdminSyncFinancialAccounts: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/admin/organizations/${organizationId}/sync/financial-accounts`;
        },
        AdminSyncBalances: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/admin/organizations/${organizationId}/sync/balances`;
        },
        AdminSyncCards: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/admin/organizations/${organizationId}/sync/cards`;
        },
        AdminManualPayout: (organizationId: number): string => {
            return `${Routes.TreasuryModule.Base}/admin/organizations/${organizationId}/payouts`;
        },
    };

    public static LegFiCore = {
        Admin: '/admin',
        ArchiveRecords: function (organizationId: number) {
            return Routes.LegFiCore.Organization(organizationId) + '/archive';
        },
        Charges: '/charges',
        Charge: function (chargeId: number) {
            return Routes.LegFiCore.Charges + '/' + chargeId;
        },
        UserChargesRollup: function (userId: number) {
            return `/users/${userId}/charges/rollup`;
        },
        Join: '/join',
        JwtPing: '/jwt-ping',
        Login: '/login',
        MemberActivations: function (organizationId: number) {
            return (
                    Routes.LegFiCore.Organization(organizationId) +
                    '/memberships/activate/resend'
            );
        },
        MemberActivation: function (memberId: number, nonce: string) {
            return '/memberships/' + memberId + '/activate/' + nonce;
        },
        KeepAlive: '/keep-alive',
        TimeoutEvent: '/auth/timeout-event',
        ClientError: '/client-errors',
        Notes: '/notes',
        Note: function (noteId: number) {
            return Routes.LegFiCore.Notes + '/' + noteId;
        },
        OrgNote: function (orgId: number, noteId: number) {
            return Routes.LegFiCore.Organization(orgId) + '/notes/' + noteId;
        },
        GenericNote: function (orgId: number, modelId: number, modelType: string) {
            return Routes.LegFiCore.Organizations + '/' + orgId + '/' + modelType + '/' + modelId + '/notes';
        },
        NonceLogin: '/nonce-login',
        PasswordResetRequest: '/password/email',
        PasswordReset: '/password/reset',
        PaymentItems: '/payment-items',
        IssueCreditForPaymentItem: function (paymentItemId: number) {
            return Routes.LegFiCore.PaymentItems + '/' + paymentItemId + '/credit';
        },
        PaymentGroups: '/payment-groups',
        PaymentGroup: function (paymentGroupId: number) {
            return Routes.LegFiCore.PaymentGroups + '/' + paymentGroupId;
        },
        PaymentLogin: '/payment-login',
        Plaid: '/plaid',
        PlaidSearch: function (context: 'auth' | 'transactions') {
            return '/plaid/search/' + context;
        },
        PlaidLinkToken: function () {
            return '/plaid/link';
        },
        PlaidTokens: '/plaid-tokens',
        Products: '/admin/products',
        Product: function (productId: number) {
            return Routes.LegFiCore.Products + '/' + productId;
        },
        PropertyManagementCompanies: '/pmc',
        PropertyManagementRegisterOrganization: function (pmcId: number) {
            return this.PropertyManagementCompanies + `/${pmcId}/register/existing`;
        },

        AdminLockbox: function () {
            return '/admin/lockbox';
        },
        AdminLockboxDepositNotices: function () {
            return this.AdminLockbox() + '/deposit-notices';
        },
        AdminLockboxRejectDetails: function (depositNoticeId: number) {
            return '/admin/lockbox/reject-details' + ((depositNoticeId === null) ? '' : '/' + depositNoticeId);
        },
        AdminLockboxPostingDetails: function (depositNoticeId: number) {
            return '/admin/lockbox/posting-details/' + depositNoticeId;
        },
        AdminLockboxPostingDetail: function (postingDetailId: number) {
            return '/admin/lockbox/posting-detail/' + postingDetailId;
        },
        AdminLockboxDepositDetails: function (depositNoticeId: number) {
            return '/admin/lockbox/deposit-details/' + depositNoticeId;
        },
        AdminDeleteDepositNotice: function (depositNoticeId: number) {
            return this.AdminLockboxDepositNotices() + '/' + depositNoticeId;
        },
        Register: '/register',
        RegisterExisting: '/register/existing',
        Renewal: '/renew',
        SendVerificationPin: '/register/send-verification',
        VerifyPin: '/register/verify',
        Sites: '/sites',
        Site: function (siteId: number): string {
            return Routes.LegFiCore.Sites + '/' + siteId;
        },
        Stats: '/stats',
        OrganizationsPendingReview: '/admin/organizations/pending',
        AdminOrganizationStates: '/admin/organizations/geographic/states',
        AdminOrganizationsByGeography: '/admin/organizations/geographic',
        AdminOrganizationsByGeographyExport: '/admin/organizations/geographic/export',
        AdminOrganizationsByGeographyMapPoints: '/admin/organizations/geographic/map-points',
        AdminOrganizationsByGeographyClusterDetail: '/admin/organizations/geographic/cluster-detail',
        AdminOrganizationsByGeographyParseQuery: '/admin/organizations/geographic/parse-query',
        SubscriptionsBreakdown: '/subscriptions/breakdown',
        SubscriptionsSummary: '/subscriptions/summary',
        SubscribedOrganizations: '/subscriptions/organizations',
        SubscriptionTemplates: '/subscription-templates',
        SubscriptionTemplate: function (subscriptionTemplateId: number) {
            return Routes.LegFiCore.SubscriptionTemplates + '/' + subscriptionTemplateId;
        },
        UpgradeToken: '/upgrade-token',

        ImpersonateUser: function (): string {
            return Routes.LegFiCore.Admin + '/impersonate';
        },
        RestoreSuperAdmin: function (): string {
            return Routes.LegFiCore.Admin + '/restore';
        },
        AdminFailedJobs: function (): string {
            return Routes.LegFiCore.Admin + '/failed-jobs';
        },
        AdminMasterOrg: function (): string {
            return Routes.LegFiCore.Admin + '/organizations/master';
        },
        AdminCreditOrg: function (): string {
            return Routes.LegFiCore.Admin + '/organizations/credit';
        },
        AdminProcessOrgPayment: function (): string {
            return Routes.LegFiCore.Admin + '/organizations/process';
        },
        AdminAssignedSalesRep: function (): string {
            return Routes.LegFiCore.Admin + '/assigned-sales-reps';
        },
        AdminAssignedSalesPatch: function (assignedSalesRepId: number): string {
            return Routes.LegFiCore.AdminAssignedSalesRep() + '/' + assignedSalesRepId;
        },
        AuxiliaryEmails: function (orgId: number, memberId: number): string {
            return Routes.LegFiCore.Membership(orgId, memberId) + '/emails';
        },
        AuxiliaryContacts: '/contacts',
        AuxiliaryContactsPatch: function (contactId: number): string {
            return Routes.LegFiCore.AuxiliaryContacts + '/' + contactId;
        },
        BillPayVendors: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/vendors';
        },
        BillPaymentHistory: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/bill-payments';
        },
        BillPaymentHistoryToVendor: function (orgId: number, vendorId: number): string {
            return Routes.LegFiCore.BillPaymentHistory(orgId) + '/' + vendorId;
        },
        BillPayVendor: function (orgId: number, vendorId: number): string {
            return Routes.LegFiCore.BillPayVendors(orgId) + '/' + vendorId;
        },
        BillPayVendorAttachments: function (orgId: number, vendorId: number): string {
            return Routes.LegFiCore.BillPayVendor(orgId, vendorId) + '/attachments';
        },
        BillPayVendorAttachmentLink: function (orgId: number, vendorId: number, attachmentId: number): string {
            return Routes.LegFiCore.BillPayVendor(orgId, vendorId) + '/attachment/' + attachmentId + '/link';
        },
        BillPayVendorDocument: function (orgId: number, vendorId: number, attachmentId: number): string {
            return Routes.LegFiCore.BillPayVendorAttachments(orgId, vendorId) + '/' + attachmentId;
        },
        BillPayBankAccounts: function (): string {
            return '/bill-pay-accounts';
        },
        BillPayBankAccount: function (accountId: number): string {
            return this.BillPayBankAccounts() + '/' + accountId;
        },
        /** @deprecated Legacy bill pay cancel - only for existing payments created before Payables migration */
        BillPayCancel: function (orgId: number, paymentId: number): string {
            return this.BillPayBankAccounts() + '/' + orgId + '/bill-payments/' + paymentId + '/cancel';
        },
        CustomFieldForOrg: function (orgId: number, type: string, fieldId: number): string {
            return Routes.LegFiCore.Organization(orgId) + `/manage-custom-fields/${type}/${fieldId}`;
        },
        CustomFieldsForOrg: function (orgId: number, type: string): string {
            return Routes.LegFiCore.Organization(orgId) + `/manage-custom-fields/${type}`;
        },
        CustomFieldValuesForMember: function (orgId: number, memberId: number): string {
            return Routes.LegFiCore.Membership(orgId, memberId) + '/custom-fields';
        },
        CustomFieldValuesForVendor: function (orgId: number, vendorId: number): string {
            return Routes.LegFiCore.BillPayVendor(orgId, vendorId) + '/custom-fields';
        },
        DeleteOrganizationLogo: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/logo';
        },
        MembershipDirectory: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/member-directory';
        },
        Memberships: '/memberships',
        AddMemberToUnits: function (memberId: number) {
            return Routes.LegFiCore.Memberships + '/' + memberId + '/units';
        },
        EnableAutopay: function (memberId: number) {
            return this.Memberships + '/' + memberId + '/autopay/enable';
        },
        DisableAutopay: function (memberId: number) {
            return this.Memberships + '/' + memberId + '/autopay/disable';
        },
        Membership: function (orgId: number, memberId: number): string {
            return '/organizations/' + orgId + '/users/' + memberId;
        },
        ResetMemberPassword: function (orgId: number, memberId: number): string {
            return Routes.LegFiCore.Membership(orgId, memberId) + '/reset-password';
        },
        UnconfirmedOwners(orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/unconfirmed-owners';
        },
        UpdateUnconfirmedOwner(orgId: number, ownerId: number): string {
            return Routes.LegFiCore.UnconfirmedOwners(orgId) + '/' + ownerId;
        },
        PeopleList: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/people-list';
        },
        MembershipBulkAdmin: function (orgId: number): string {
            return '/organizations/' + orgId + '/users/admin';
        },
        Members: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/users';
        },
        LockMembers: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/users/lock';
        },
        UnlockMembers: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/users/unlock';
        },
        Team: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/team';
        },
        TeamMember: function (orgId: number, memberId: number): string {
            return Routes.LegFiCore.Team(orgId) + '/' + memberId;
        },
        Lob: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/lob';
        },
        LobOrgBillPayMail: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/lob/bill-pay';
        },
        LobOrgInvoiceMail: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/lob/invoice';
        },
        LobOrgPDFMail: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/lob/pdf';
        },
        LobOrgViolationMail: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/lob/violation';
        },
        LobOrgVotingMail: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/lob/voting';
        },
        LobUserMailPref: function (orgId: number, userId: number): string {
            return Routes.LegFiCore.Organization(orgId) + `/lob/user/${userId}/mail-pref`;
        },
        MembersRestore: function (orgId: number): string {
            return Routes.LegFiCore.Members(orgId) + '/restore';
        },
        MemberPaymentGroups: function (orgId: number, memberId: number): string {
            return Routes.LegFiCore.Membership(orgId, memberId) + '/payment-groups';
        },
        MemberSummary: function (orgId: number, memberId: number): string {
            return Routes.LegFiCore.Membership(orgId, memberId) + '/summary';
        },
        VoterUser: '/user/organizations',
        VoterUserOrganization: function (orgId: number): string {
            return Routes.LegFiCore.VoterUser + '/' + orgId;
        },
        Organizations: '/organizations',
        Organization: function (orgId: number): string {
            return Routes.LegFiCore.Organizations + '/' + orgId;
        },
        OrganizationLockbox: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/lockbox';
        },
        RestoreOrganization: function (orgId: number): string {
            return Routes.LegFiCore.Organizations + '/' + orgId + '/restore';
        },
        OrganizationDashboardWidgets: function (orgId: number, memberId: number): string {
            return Routes.LegFiCore.OrgDashboard(orgId) + '/widgets/' + memberId;
        },
        OrganizationPreferences: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/preferences';
        },
        VerifyOrganization: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/verify';
        },
        OrganizationRates: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/rates';
        },
        OrganizationExpirationCheckout: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/correct';
        },
        OrganizationPayable: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/payables';
        },
        OrganizationPayableDetails: function (orgId: number, payableId: number): string {
            return Routes.LegFiCore.OrganizationPayable(orgId) + '/' + payableId + '/details';
        },
        OrganizationReplyToEmail: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/reply-email';
        },
        SearchOrgList: function (): string {
            return Routes.LegFiCore.Organizations + '/search';
        },
        SendPaymentLoginEmail: function (): string {
            return Routes.LegFiCore.Memberships + '/payment-login';
        },
        SimpleOrgList: function (): string {
            return Routes.LegFiCore.Organizations + '/simple';
        },
        SimpleOrgInfo: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/simple-info';
        },
        OrgDefaultPaymentMethod: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/default-payment-method';
        },
        OrgDepositAccounts: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/bank-accounts/deposit';
        },
        OrgDepositAccount: function (orgId: number, depositAccountId: number): string {
            return Routes.LegFiCore.OrgDepositAccounts(orgId) + '/' + depositAccountId;
        },
        RefreshOrgDepositAccount: function (orgId: number, depositAccountId: number): string {
            return Routes.LegFiCore.OrgDepositAccount(orgId, depositAccountId) + '/refresh';
        },
        OrgDepositAccountPendingPayments: function (orgId: number, depositAccountId: number) {
            return this.OrgDepositAccount(orgId, depositAccountId) + '/pending';
        },
        OrgPaymentMethods: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/payment-methods';
        },
        OrgBankAccounts: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/bank-accounts';
        },
        OrgBankAccount: function (orgId: number, bankAccountId: number): string {
            return Routes.LegFiCore.OrgBankAccounts(orgId) + '/' + bankAccountId;
        },
        PlaidBalanceHistory: function (orgId: number, bankAccountId: number): string {
            return Routes.LegFiCore.OrgBankAccounts(orgId) + '/' + bankAccountId + '/plaid-balance-history';
        },
        CreateOrgBankAccountAsset: function (orgId: number, bankAccountId: number): string {
            return Routes.LegFiCore.OrgBankAccounts(orgId) + '/' + bankAccountId + '/asset';
        },
        UpdatePayabliDefault: function (orgId: number, bankAccountId: number): string {
            return Routes.LegFiCore.OrgBankAccounts(orgId) + '/' + bankAccountId + '/payabli-default';
        },
        LinkedAccountPlaidFeed: function (orgId: number, linkedAccountId: number): string {
            return Routes.LegFiCore.OrgBankAccounts(orgId) + '/' + linkedAccountId + '/plaid-feed';
        },
        LinkedAccountPlaidImport: function (orgId: number, linkedAccountId: number): string {
            return Routes.LegFiCore.OrgBankAccounts(orgId) + '/' + linkedAccountId + '/plaid-import';
        },
        OrgCards: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/credit-cards';
        },
        OrgCard: function (orgId: number, cardId: number) {
            return Routes.LegFiCore.OrgCards(orgId) + '/' + cardId;
        },
        OrgCommunications: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/communications';
        },
        OrgCommunicationsView: function (orgId: number, activityId: number): string {
            return Routes.LegFiCore.OrgCommunications(orgId) + '/' + activityId + '/view';
        },
        OrgCommsSendSampleEmail: function (orgId: number, memberId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/email/send-sample/' + memberId;
        },
        OrgCommsSampleEmail: function (orgId: number, memberId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/email/sample/' + memberId;
        },
        OrgCommsEmail: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/email/members';
        },
        OrgCommsCalls: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/calls';
        },
        OrgCommsTexts: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/sms';
        },
        OrgViolationsPdfDownload: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/violation-pdfs';
        },
        // PDF Export endpoints (async background processing)
        OrgPdfExports: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/pdf-exports';
        },
        OrgPdfExportStatus: function (orgId: number, exportId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/pdf-exports/' + exportId;
        },
        OrgPdfExportDownload: function (orgId: number, exportId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/pdf-exports/' + exportId + '/download';
        },
        OrgPdfExportRetry: function (orgId: number, exportId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/pdf-exports/' + exportId + '/retry';
        },
        // Unit Deletion endpoints (async background processing with progress)
        OrgUnitDeletions: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/unit-deletions';
        },
        OrgUnitDeletionStatus: function (orgId: number, deletionId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/unit-deletions/' + deletionId;
        },
        OrgUnitDeletionRetry: function (orgId: number, deletionId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/unit-deletions/' + deletionId + '/retry';
        },
        OrgCommsLogPdfDownload: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/communications/generate-pdfs-for-email';
        },
        OrgCommsLogV2PdfDownload: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/communications/v2/generate-pdfs-for-email';
        },
        OrgStatementPdfPreviewForUnit: function (orgId: number, unitId: number, includeFuture: boolean): string {
            return Routes.LegFiCore.Organization(orgId) + '/statement-pdf/' + unitId + '/' + (includeFuture ? 1 : 0);
        },
        OrgCommsLogV2Filters: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/communications/v2/filters';
        },
        OrgEmailStatusGraph: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/communications/email-status-graph';
        },
        OrgEmailStatusCounts: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/communications/email-status-counts';
        },
        OrgEmailCategoryCounts: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/communications/email-category-counts';
        },
        OrgCommCountsSmsCalls: function (orgId: number, which: string): string {
            return Routes.LegFiCore.Organization(orgId) + '/communications/donut-counts/' + which;
        },
        OrgPaperMailGraphCounts: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/communications/paper-mail-graph-counts';
        },
        OrgPaperMailStatusCounts: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/communications/paper-mail-status-counts';
        },
        OrgSurveys: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/surveys';
        },
        OrgSurvey: function (orgId: number, surveyId: number): string {
            return Routes.LegFiCore.OrgSurveys(orgId) + '/' + surveyId;
        },
        OrgCopySurvey: function (orgId: number, surveyId: number): string {
            return Routes.LegFiCore.OrgSurvey(orgId, surveyId) + '/copy';
        },
        OrgSurveyResults: function (orgId: number, surveyId: number): string {
            return Routes.LegFiCore.OrgSurveys(orgId) + '/' + surveyId + '/results';
        },
        OrgSurveyResponse: function (orgId: number, surveyId: number): string {
            return Routes.LegFiCore.OrgSurveys(orgId) + '/' + surveyId + '/response';
        },
        OrgSurveyShare: function (orgId: number, surveyId: number): string {
            return Routes.LegFiCore.OrgSurvey(orgId, surveyId) + '/share';
        },
        CloseOrgSurvey: function (orgId: number, surveyId: number): string {
            return Routes.LegFiCore.OrgSurvey(orgId, surveyId) + '/close';
        },
        ReopenOrgSurvey: function (orgId: number, surveyId: number): string {
            return Routes.LegFiCore.OrgSurvey(orgId, surveyId) + '/open';
        },
        CopyNewResourceDocuments: function (orgId: number): string {
            return Routes.LegFiCore.OrgDocuments(orgId) + '/copy-new-resources';
        },
        BulkOrgDocuments: function (orgId: number): string {
            return Routes.LegFiCore.OrgDocuments(orgId) + '/bulk-download';
        },
        OrgDocuments: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/documents';
        },
        OrgDocument: function (orgId: number, documentId: number): string {
            return Routes.LegFiCore.OrgDocuments(orgId) + '/' + documentId;
        },
        OrgDocumentDetails: function (orgId: number, documentId: number): string {
            return Routes.LegFiCore.OrgDocuments(orgId) + '/' + documentId + '/details';
        },
        OrgUnitDocumentDetails: function (orgId: number, documentId: number, unitId: number): string {
            return Routes.LegFiCore.OrgDocumentDetails(orgId, documentId) + '/' + unitId;
        },
        DownloadOrgDocument: function (orgId: number, documentId: number): string {
            return Routes.LegFiCore.OrgDocuments(orgId) + '/' + documentId + '/download';
        },
        OrgSubscriptions: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/subscriptions';
        },
        OrgSubscription: function (orgId: number, subscriptionId: number): string {
            return Routes.LegFiCore.OrgSubscriptions(orgId) + '/' + subscriptionId;
        },
        DocumentPublicize: function (orgId: number, documentId: number): string {
            return Routes.LegFiCore.OrgDocument(orgId, documentId) + '/public';
        },
        DocumentUpdate: function (orgId: number, documentId: number): string {
            return Routes.LegFiCore.OrgDocument(orgId, documentId) + '/update';
        },
        DocumentAttachEmail: function (orgId: number): string {
            return Routes.LegFiCore.OrgDocuments(orgId) + '/attach';
        },
        DocumentDeleteEmailAttach: function (orgId: number, fileId: number): string {
            return Routes.LegFiCore.OrgDocuments(orgId) + '/attach/' + fileId;
        },
        RestoreHiddenDocuments: function (orgId: number): string {
            return Routes.LegFiCore.OrgDocuments(orgId) + '/restore-hidden';
        },
        TinyMCEAIProxy: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai/tinymce/generate';
        },
        AIWebsiteChatbot: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai/admin-chatbot/website-tester/query';
        },
        UnitDocuments: function (orgId: number, unitId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/units/' + unitId + '/documents';
        },
        OrgDashboard: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/dashboard';
        },
        Collections: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/collections';
        },
        CollectionsPastDueEmails: function (orgId: number): string {
            return Routes.LegFiCore.Collections(orgId) + '/past-due-emails';
        },
        CollectionProgress: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/graph/collection-progress';
        },
        PaymentsGraph: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/graph/payments';
        },
        OrgModules: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/modules';
        },
        Budgets: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/budgets';
        },
        BudgetCopy: function (orgId: number): string {
            return Routes.LegFiCore.Budgets(orgId) + '/copy';
        },
        BudgetDashboard: function (orgId: number): string {
            return Routes.LegFiCore.Budgets(orgId) + '/dashboard';
        },
        BudgetDetail: function (orgId: number): string {
            return Routes.LegFiCore.Budgets(orgId) + '/details';
        },
        BudgetItem: function (orgId: number, budgetItemId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/budgetItem/' + budgetItemId;
        },
        BudgetDistribute: function (orgId: number): string {
            return Routes.LegFiCore.Budgets(orgId) + '/distribute';
        },
        // Draft Budgets (AIBudget module)
        DraftBudgets: function (orgId: number): string {
            return '/ai-budget/organizations/' + orgId + '/drafts';
        },
        DraftBudgetCoverage: function (orgId: number): string {
            return Routes.LegFiCore.DraftBudgets(orgId) + '/coverage';
        },
        DraftBudgetsArchived: function (orgId: number): string {
            return Routes.LegFiCore.DraftBudgets(orgId) + '/archived';
        },
        DraftBudgetFromScratch: function (orgId: number): string {
            return Routes.LegFiCore.DraftBudgets(orgId) + '/from-scratch';
        },
        DraftBudgetFromHistorical: function (orgId: number): string {
            return Routes.LegFiCore.DraftBudgets(orgId) + '/from-historical';
        },
        DraftBudgetFromUpload: function (orgId: number): string {
            return Routes.LegFiCore.DraftBudgets(orgId) + '/from-upload';
        },
        DraftBudgetProcessUpload: function (orgId: number): string {
            return Routes.LegFiCore.DraftBudgets(orgId) + '/process-upload';
        },
        DraftBudgetProcessUploadFromDocuments: function (orgId: number): string {
            return Routes.LegFiCore.DraftBudgets(orgId) + '/process-upload-from-documents';
        },
        DraftBudget: function (orgId: number, draftId: number): string {
            return Routes.LegFiCore.DraftBudgets(orgId) + '/' + draftId;
        },
        DraftBudgetCheckOverlap: function (orgId: number, draftId: number): string {
            return Routes.LegFiCore.DraftBudget(orgId, draftId) + '/check-overlap';
        },
        DraftBudgetRestore: function (orgId: number, draftId: number): string {
            return Routes.LegFiCore.DraftBudget(orgId, draftId) + '/restore';
        },
        DraftBudgetPublish: function (orgId: number, draftId: number): string {
            return Routes.LegFiCore.DraftBudget(orgId, draftId) + '/publish';
        },
        DraftBudgetUnpublish: function (orgId: number, draftId: number): string {
            return Routes.LegFiCore.DraftBudget(orgId, draftId) + '/unpublish';
        },
        DraftBudgetItems: function (orgId: number, draftId: number): string {
            return Routes.LegFiCore.DraftBudget(orgId, draftId) + '/items';
        },
        DraftBudgetItem: function (orgId: number, draftId: number, itemId: number): string {
            return Routes.LegFiCore.DraftBudgetItems(orgId, draftId) + '/' + itemId;
        },
        DraftBudgetDistribute: function (orgId: number, draftId: number): string {
            return Routes.LegFiCore.DraftBudgetItems(orgId, draftId) + '/distribute';
        },
        DraftBudgetReset: function (orgId: number, draftId: number): string {
            return Routes.LegFiCore.DraftBudget(orgId, draftId) + '/reset';
        },
        DraftBudgetExportCsv: function (orgId: number, draftId: number): string {
            return Routes.LegFiCore.DraftBudget(orgId, draftId) + '/export-csv';
        },
        DraftBudgetExportPdf: function (orgId: number, draftId: number): string {
            return Routes.LegFiCore.DraftBudget(orgId, draftId) + '/export-pdf';
        },

        // AIBudget module endpoints for budget overview dashboard
        AIBudgetSummary: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai-budget/summary';
        },
        AIBudgetRevenueDistribution: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai-budget/revenue-distribution';
        },
        AIBudgetExpenseDistribution: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai-budget/expense-distribution';
        },
        AIBudgetMonthlyIncome: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai-budget/monthly-income';
        },
        AIBudgetMonthlyExpense: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai-budget/monthly-expense';
        },
        AIBudgetRevenueAnalysis: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai-budget/revenue-analysis';
        },
        AIBudgetExpenseAnalysis: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai-budget/expense-analysis';
        },
        AIBudgetYoYSupplement: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai-budget/yoy-supplement';
        },

        Category: function (orgId: number, categoryId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/categories/' + categoryId;
        },
        CategoryItemCount: function (orgId: number, categoryId: number): string {
            return Routes.LegFiCore.Category(orgId, categoryId) + '/count';
        },
        Categories: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/categories';
        },
        Owners: '/owners',
        Owner: function (ownerId: number): string {
            return Routes.LegFiCore.Owners + '/' + ownerId;
        },
        Occupancy: function (id: number): string {
            return '/occupancies/' + id;
        },
        UnitOwners: function (unitId: number): string {
            return Routes.LegFiCore.Units + '/' + unitId + '/owners';
        },
        UnitPreviousOwners: function (unitId: number): string {
            return Routes.LegFiCore.UnitOwners(unitId) + '/previous';
        },
        AdminPaymentGroups: function (): string {
            return Routes.LegFiCore.Admin + '/payment-groups';
        },
        AdminPaymentFilters: function (): string {
            return this.AdminPaymentGroups() + '/filters';
        },
        IncomingPaymentGroups: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/payment-groups';
        },
        IncomingPaymentGroupsDashboard: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/payment-groups/dashboard';
        },
        IncomingPaymentsFilters: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/payment-groups/filters';
        },
        IncomingPaymentsReceipts: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/payment-groups/receipts';
        },
        IncomingPaymentGroup: function (orgId: number, paymentId: number): string {
            return Routes.LegFiCore.IncomingPaymentGroups(orgId) + '/' + paymentId;
        },
        PaymentGroupReceipt: function (orgId: number, paymentGroupId: number): string {
            return Routes.LegFiCore.IncomingPaymentGroup(orgId, paymentGroupId) + '/receipt';
        },

        ReturnPaymentGroup: function (orgId: number, paymentId: number): string {
            return Routes.LegFiCore.IncomingPaymentGroup(orgId, paymentId) + '/return';
        },
        ReturnPaymentGroups: function (orgId: number): string {
            return Routes.LegFiCore.IncomingPaymentGroups(orgId) + '/return';
        },

        DeletePaymentGroup: function (orgId: number, paymentId: number, reason: string): string {
            return Routes.LegFiCore.IncomingPaymentGroup(orgId, paymentId) + '?reason=' + encodeURIComponent(reason);
        },
        DeletePaymentGroups: function (orgId: number, paymentIds: number[], reason: string): string {
            return Routes.LegFiCore.IncomingPaymentGroups(orgId) + '?reason=' + encodeURIComponent(reason) + '&payments=' + paymentIds.join(',');
        },

        UpdatePaymentGroupTotals: function (organizationId: number, paymentGroupId: number): string {
            return Routes.LegFiCore.IncomingPaymentGroup(organizationId, paymentGroupId) + '/update-totals';
        },
        IssuedCharges: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/charges/issued';
        },
        IssuedChargesRollup: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/charges/issued/rollup';
        },
        IssuedChargesByTitle: function (orgId: number): string {
            return Routes.LegFiCore.IssuedCharges(orgId) + '/title';
        },
        IssuedChargesCopyList: function (orgId: number): string {
            return Routes.LegFiCore.IssuedCharges(orgId) + '/copy';
        },
        IssuedChargesFilters: function (orgId: number): string {
            return Routes.LegFiCore.IssuedCharges(orgId) + '/filters';
        },
        IssuedChargesOverview: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/issuedChargesOverview';
        },
        IssuedChargesForgive: function (): string {
            return Routes.LegFiCore.Charges + '/forgive';
        },
        IssuedChargesPaymentPlan: function (): string {
            return Routes.LegFiCore.Charges + '/payment-plan';
        },
        IssuedChargesPreview: function (): string {
            return Routes.LegFiCore.Charges + '/preview';
        },
        IssuedChargesReinstate: function (): string {
            return Routes.LegFiCore.Charges + '/reinstate';
        },
        IssuedChargesSendInvoice: function (): string {
            return Routes.LegFiCore.Charges + '/send-invoices';
        },
        RecurringChargeTemplates: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/charges/recurring-templates';
        },
        ExpectedCharges: function (orgId: number, templateId: number): string {
            return Routes.LegFiCore.RecurringChargeTemplates(orgId) + '/' + templateId + '/expected-charges';
        },
        RecurringChargeTemplatesForUnit: function (orgId: number, unitId: number) {
            return Routes.LegFiCore.OrganizationUnit(orgId, unitId) + '/charges/recurring-templates';
        },
        PauseRecurringChargeTemplatesForUnit: function (orgId: number, unitId: number) {
            return Routes.LegFiCore.RecurringChargeTemplatesForUnit(orgId, unitId) + '/pause';
        },
        UnpauseRecurringChargeTemplatesForUnit: function (orgId: number, unitId: number) {
            return Routes.LegFiCore.RecurringChargeTemplatesForUnit(orgId, unitId) + '/unpause';
        },
        RecurringChargeTemplate: function (orgId: number, templateId: number): string {
            return Routes.LegFiCore.RecurringChargeTemplates(orgId) + '/' + templateId;
        },
        RecurringChargeCharges: function (orgId: number, templateId: number): string {
            return Routes.LegFiCore.RecurringChargeTemplate(orgId, templateId) + '/charges';
        },
        RecurringChargeTemplatesPause: function (orgId: number): string {
            return Routes.LegFiCore.RecurringChargeTemplates(orgId) + '/pause';
        },
        RecurringChargeTemplatesUnpause: function (orgId: number): string {
            return Routes.LegFiCore.RecurringChargeTemplates(orgId) + '/unpause';
        },
        LockboxPaymentGroups: function (orgId: number) {
            return Routes.LegFiCore.Organization(orgId) + '/payment-groups/lockbox';
        },
        OfflinePaymentGroups: function (orgId: number) {
            return Routes.LegFiCore.Organization(orgId) + '/payment-groups/offline';
        },
        LinkPaymentGroups: function (orgId: number) {
            return Routes.LegFiCore.Organization(orgId) + '/payment-groups/link';
        },
        UnlinkPaymentGroups: function (orgId: number) {
            return Routes.LegFiCore.Organization(orgId) + '/payment-groups/unlink';
        },
        PaymentLinkingStatus: function (orgId: number, batchId: string) {
            return Routes.LegFiCore.Organization(orgId) + '/payment-groups/linking-status/' + batchId;
        },
        ActivePaymentLinkingBatches: function (orgId: number) {
            return Routes.LegFiCore.Organization(orgId) + '/payment-groups/active-linking-batches';
        },
        ReceivedCharges: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/charges/received';
        },
        ARActivity: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/charges/activity';
        },
        MasterReports: function () {
            return '/reporting/v2/master';
        },
        Reports: function (orgId: number) {
            return Routes.LegFiCore.Organization(orgId) + '/reports';
        },
        ReportsHomepage: function () {
            return '/reports/home';
        },

        ReportsConfig: function () {
            return '/reports/config';
        },

        ReportPackets: function () {
            return '/report-packets';
        },

        ReportPacket: function (reportPacketId: number) {
            return this.ReportPackets() + '/' + reportPacketId;
        },

        ScheduledReports: function () {
            return '/scheduled-reports';
        },

        ScheduledReportsConfig: function () {
            return '/scheduled-reports/config';
        },

        ScheduledReport: function (scheduledReportId: number) {
            return this.ScheduledReports() + '/' + scheduledReportId;
        },

        ScheduledReportPause: function (scheduledReportId: number) {
            return this.ScheduledReport(scheduledReportId) + '/pause';
        },

        ScheduledReportResume: function (scheduledReportId: number) {
            return this.ScheduledReport(scheduledReportId) + '/resume';
        },

        ScheduledReportTrigger: function (scheduledReportId: number) {
            return this.ScheduledReport(scheduledReportId) + '/trigger';
        },

        ScheduledReportsForSchedulable: function () {
            return '/scheduled-reports/for-schedulable';
        },

        SavedReports: function () {
            return '/saved-reports';
        },

        SavedReport: function (savedReportId: number) {
            return this.SavedReports() + '/' + savedReportId;
        },

        SavedReportDownload: function (savedReportId: number) {
            return this.SavedReport(savedReportId) + '/download';
        },

        SavedReportShare: function (savedReportId: number) {
            return this.SavedReport(savedReportId) + '/share';
        },

        SavedReportEmail: function (savedReportId: number) {
            return this.SavedReport(savedReportId) + '/email';
        },

        // Custom Report Templates
        CustomReportTemplates: function () {
            return '/custom-report-templates';
        },

        CustomReportTemplate: function (templateId: number) {
            return this.CustomReportTemplates() + '/' + templateId;
        },

        // Custom Report Template Favorites
        CustomReportTemplateFavorites: function () {
            return '/custom-report-template-favorites';
        },

        CustomReportTemplateFavorite: function (templateId: number) {
            return this.CustomReportTemplateFavorites() + '/' + templateId;
        },

        ReportsGeneralLedger: function (orgId: number, format: string) {
            return Routes.LegFiCore.Reports(orgId)
                    + '/general-ledger/'
                    + format;
        },
        ReportsGenerateGeneralLedger: function (orgId: number) {
            return Routes.LegFiCore.Reports(orgId)
                    + '/general-ledger/generate';
        },

        ReportsAccountRegister: function (orgId: number, format: string) {
            return Routes.LegFiCore.Reports(orgId)
                    + '/account-register/'
                    + format;
        },

        ReportsBalanceSheetDetails: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId)
                    + '/balance-sheet/'
                    + (pdf ? 1 : 0);
        },

        ReportsTrialBalance: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId)
                    + '/trial-balance/'
                    + (pdf ? 1 : 0);
        },

        ReportsCashFlowStatement: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId)
                    + '/cash-flow-statement/'
                    + (pdf ? 1 : 0);
        },

        ReportsProfitVsLoss: function (orgId: number, report: string, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId)
                    + '/profit-vs-loss/' + report + '/'
                    + (pdf ? 1 : 0);
        },
        ReportsProfitVsLossSummary: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.ReportsProfitVsLoss(orgId, 'summary', pdf);
        },
        ReportsProfitVsLossByMonth: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.ReportsProfitVsLoss(orgId, 'by-month', pdf);
        },
        ReportsProfitVsLossDetail: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.ReportsProfitVsLoss(orgId, 'details', pdf);
        },
        ReportsProfitVsLossPercentage: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.ReportsProfitVsLoss(orgId, 'percentage', pdf);
        },

        ReportsJournalEntries: function (orgId: number, format: string = 'json') {
            return Routes.LegFiCore.Reports(orgId)
                    + '/journal-entries/'
                    + format;
        },
        ReportReconciliations: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId)
                    + '/reconciliations/'
                    + (pdf ? 1 : 0);
        },

        ReportsOrganizations: function (format: string) {
            return Routes.LegFiCore.MasterReports()
                    + '/organizations/'
                    + format;
        },
        ReportsMasterBilling: function (format: string) {
            return Routes.LegFiCore.MasterReports()
                    + '/master-billing/'
                    + format;
        },
        ReportsApiPerformance: function (format: string) {
            return Routes.LegFiCore.MasterReports()
                    + '/api-performance/'
                    + format;
        },
        ReportsNonbilled: function (format: string) {
            return Routes.LegFiCore.MasterReports()
                    + '/non-billed/'
                    + format;
        },
        ReportsBookkeepingProspects: function (format: string) {
            return Routes.LegFiCore.MasterReports()
                    + '/bookkeeping-prospects/'
                    + format;
        },
        ReportsChurn: function (format: string) {
            return Routes.LegFiCore.MasterReports()
                    + '/churn/'
                    + format;
        },
        ReportsNewCustomers: function (format: string) {
            return Routes.LegFiCore.MasterReports()
                    + '/new-customers/'
                    + format;
        },
        ReportsSignUps: function (format: string) {
            return Routes.LegFiCore.MasterReports()
                    + '/sign-ups/'
                    + format;
        },

        ReportsBudgetVsActual: function (orgId: number, report: string, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId)
                    + '/budget-vs-actual/' + report + '/'
                    + (pdf ? 1 : 0);
        },

        ReportsBudgetSummary: function (orgId: number, mode: 'summary' | 'yearly' | 'monthly', pdf: boolean) {
            return `${Routes.LegFiCore.Reports(orgId)}/budget-summary/${mode}/${(pdf ? 1 : 0)}`;
        },

        ReportsVendorBalanceSummary: function (orgId: number, pdf: boolean = false) {
            return `${Routes.LegFiCore.Reports(orgId)}/vendor-balance-summary/${(pdf ? 1 : 0)}`;
        },

        ReportsVendorBalanceDetail: function (orgId: number, pdf: boolean = false) {
            return `${Routes.LegFiCore.Reports(orgId)}/vendor-balance-detail/${(pdf ? 1 : 0)}`;
        },

        ReportsBudgetVsActualSummary: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.ReportsBudgetVsActual(orgId, 'summary', pdf);
        },

        ReportsBudgetVsActualByMonth: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.ReportsBudgetVsActual(orgId, 'by-month', pdf);
        },

        ReportUnitBalanceList(orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/unit-balance/list/' + (pdf ? 1 : 0);
        },
        ReportUnitBalanceSummary(orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/unit-balance/summary/' + (pdf ? 1 : 0);
        },
        ReportUnitBalanceDetail(orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/unit-balance/detail/' + (pdf ? 1 : 0);
        },
        ReportDelinquentAccountsSummary(orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/delinquent-accounts/summary/' + (pdf ? 1 : 0);
        },
        ReportDelinquentAccountsDetail(orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/delinquent-accounts/detail/' + (pdf ? 1 : 0);
        },
        ReportInvoicesByUnit(orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/invoices/by-unit/' + (pdf ? 1 : 0);
        },
        ReportInvoicesByRevenueCategory(orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/invoices/by-revenue-category/' + (pdf ? 1 : 0);
        },
        ReportInvoicesByTitle(orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/invoices/by-title/' + (pdf ? 1 : 0);
        },
        ReportsReceiptsDetail: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/payments/receipts/' + (pdf ? 1 : 0);
        },
        ReportsPaymentsByUnit: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/payments/by-unit/' + (pdf ? 1 : 0);
        },
        ReportsPrepaymentsByUnit: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/prepayments/by-unit/' + (pdf ? 1 : 0);
        },
        ReportsNewRecurringCharges: function (orgId: number, format: string) {
            return Routes.LegFiCore.Reports(orgId) + '/recurring/new/' + format;
        },
        ReportReturnedPayments: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/returned-payments/' + (pdf ? 1 : 0);
        },
        ReportDepositDetails: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/deposit-details/' + (pdf ? 1 : 0);
        },
        ReportViolations: function (orgId: number, format: string) {
            return Routes.LegFiCore.Reports(orgId) + '/violations/' + format;
        },
        ReportsCheckRegister: function (orgId: number, format: string) {
            return Routes.LegFiCore.Reports(orgId) + '/check-register/' + format;
        },
        ReportsCheckRegisterFields: function (orgId: number) {
            return Routes.LegFiCore.Reports(orgId) + '/check-register/fields';
        },
        ReportsCheckRegisterLegacy: function (orgId: number, format: string) {
            return Routes.LegFiCore.Reports(orgId) + '/check-register-legacy/' + format;
        },
        ReportsCheckRegisterLegacyHasRecords: function (orgId: number) {
            return Routes.LegFiCore.Reports(orgId) + '/check-register-legacy/has-records';
        },
        ReportsExpensesByVendor: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/expenses/by-vendor/' + (pdf ? 1 : 0);
        },
        ReportsVendorOverview: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/expenses/vendor-overview/' + (pdf ? 1 : 0);
        },
        ReportsVendorOverviewFields: function (orgId: number) {
            return Routes.LegFiCore.Reports(orgId) + '/expenses/vendor-overview/fields';
        },
        ReportsSubscriptionHistory: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/expenses/subscription-history/' + (pdf ? 1 : 0);
        },
        ReportsExpensesByCategory: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/expenses/by-category/' + (pdf ? 1 : 0);
        },
        ReportsExpensesDetail: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/expenses/detail/' + (pdf ? 1 : 0);
        },
        ReportsAgingOfAccounts: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/aging-of-accounts/' + (pdf ? 1 : 0);
        },
        ReportsVendorAgingOfAccounts: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/vendor-aging-of-accounts/' + (pdf ? 1 : 0);
        },
        ReportsVendorAgingOfAccountsDetail: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/vendor-aging-of-accounts-detail/' + (pdf ? 1 : 0);
        },
        ReportsCustomFields: function (orgId: number) {
            return Routes.LegFiCore.Reports(orgId) + '/custom/fields';
        },
        ReportsCustom: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/custom/' + (pdf ? 1 : 0);
        },
        ReportsContactInfo: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/unit-contact-info/' + (pdf ? 1 : 0);
        },
        ReportsVendorInfo: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/vendor-info/' + (pdf ? 1 : 0);
        },
        ReportsOwnershipChange: function (orgId: number, pdf: boolean) {
            return Routes.LegFiCore.Reports(orgId) + '/ownership-change/' + (pdf ? 1 : 0);
        },
        BankReconciliation: function (orgId: number, reconciliationId?: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/reconciliation' + (reconciliationId
                    ? `/${reconciliationId}`
                    : '');
        },
        BankReconciliationByAccount: function (orgId: number, accountId?: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/reconciliation/account/' + accountId;
        },
        BankReconciliationTransaction: function (
                orgId: number,
                reconciliationId: number,
                transactionId: number,
        ): string {
            return Routes.LegFiCore.BankReconciliation(orgId, reconciliationId) + '/transaction/' + transactionId;
        },
        BankReconciliationMissingItem: function (
                orgId: number,
                reconciliationId: number,
                missingItemId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) + '/ai-bank-rec/reconciliations/' + reconciliationId + '/missing-items/' + missingItemId;
        },
        BankReconciliationReport: function (orgId: number, reconciliationId: number, pdf: boolean = false): string {
            return Routes.LegFiCore.Organization(orgId) + '/reconciliations/' + reconciliationId + '/report' + (pdf ? '/pdf' : '');
        },
        // AIBankRec module endpoint for V2 landing page list
        BankReconciliationsList: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/reconciliations';
        },
        Transaction: function (orgId: number, transactionId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/transactions/' + transactionId;
        },
        Transactions: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/transactions';
        },
        TransactionsBatch: function (orgId: number): string {
            return Routes.LegFiCore.Transactions(orgId) + '/batch';
        },
        DeleteTransactions: function (orgId: number): string {
            return Routes.LegFiCore.Transactions(orgId) + '/delete';
        },
        RestoreTransactions: function (orgId: number): string {
            return Routes.LegFiCore.Transactions(orgId) + '/restore';
        },
        TransactionFilters: function (orgId: number): string {
            return Routes.LegFiCore.Transactions(orgId) + '/transactionFilters';
        },
        TransactionsCategory: function (orgId: number, catId: number): string {
            return Routes.LegFiCore.Transactions(orgId) + '/category/' + catId;
        },
        TransactionCategoryTotals: function (orgId: number): string {
            return Routes.LegFiCore.Transactions(orgId) + '/categoryTotals';
        },
        TransactionAttachments: function (orgId: number, transactionId: number): string {
            return Routes.LegFiCore.Transaction(orgId, transactionId) + '/attachments';
        },
        TransactionsMerge: function (orgId: number): string {
            return Routes.LegFiCore.Transactions(orgId) + '/merge';
        },
        TransactionsBulkImport: function (orgId: number): string {
            return '/organizations/' + orgId + '/transactions/bulk-import';
        },
        TransactionsBulkImportSupportedColumns: function (orgId: number): string {
            return Routes.LegFiCore.TransactionsBulkImport(orgId) + '/columns';
        },
        TransactionsBulkImportFileCheck: function (orgId: number): string {
            return Routes.LegFiCore.TransactionsBulkImport(orgId) + '/file-check';
        },
        User: function (userId: number): string {
            return '/users/' + userId;
        },
        SwitchContext: function (orgId: number): string {
            return '/users/switch-account/' + orgId;
        },
        PaymentBankAccounts: '/payment-bank-accounts',
        PaymentBankAccount: function (accountId: number) {
            return this.PaymentBankAccounts + '/' + accountId;
        },
        VerifyPaymentBankAccount: function (accountId: number) {
            return this.PaymentBankAccount(accountId) + '/verify';
        },
        UserPaymentMethods: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/payment-methods';
        },
        UserPaymentBankAccounts: function (userId: number) {
            return this.User(userId) + '/payment-bank-accounts';
        },
        UserPaymentBankAccount: function (userId: number, accountId: number) {
            return this.UserPaymentBankAccounts(userId) + '/' + accountId;
        },
        OrgPaymentBankAccounts: function (orgId: number) {
            return this.Organization(orgId) + '/payment-bank-accounts';
        },
        OrgPaymentBankAccount: function (orgId: number, accountId: number) {
            return this.OrgPaymentBankAccounts(orgId) + '/' + accountId;
        },

        UserSmsAuthSetup: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/2fa/sms/setup';
        },
        UserSmsAuthConfirm: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/2fa/sms/confirm';
        },
        UserSmsAuthSend: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/2fa/sms/send';
        },
        UserSmsAuthDisable: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/2fa/sms/disable';
        },
        UserSmsAuthValidate: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/2fa/sms/validate';
        },
        UserGoogleAuthDisable: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/2fa/google/disable';
        },
        UserGoogleAuthSetup: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/2fa/google/setup';
        },
        UserGoogleAuthValidate: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/2fa/google/validate';
        },
        UserTrustedDevices: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/trusted-devices';
        },
        UserTrustedDevice: function (userId: number, tokenId: number): string {
            return Routes.LegFiCore.UserTrustedDevices(userId) + '/' + tokenId;
        },
        UserCards: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/credit-cards';
        },
        UserCard: function (userId: number, cardId: number): string {
            return Routes.LegFiCore.UserCards(userId) + '/' + cardId;
        },
        UserCheckout: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/checkout';
        },
        UserCharges: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/charges';
        },
        UserChangePassword: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/change-password';
        },
        UserNotificationPreferences: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/notification-preferences';
        },
        UserNotificationPreference: function (userId: number, preferenceId: number): string {
            return Routes.LegFiCore.User(userId) + '/notification-preferences/' + preferenceId.toString();
        },
        UserMemberships: function (userId: number): string {
            return Routes.LegFiCore.User(userId) + '/memberships';
        },
        // Calendar Events
        Calendar: '/calendar',
        CalendarEvents: function (organizationId: number): string {
            return Routes.LegFiCore.Organizations + '/' + organizationId + '/calendar/events';
        },
        CalendarEvent: function (organizationId: number, eventId: number): string {
            return Routes.LegFiCore.CalendarEvents(organizationId) + '/' + eventId;
        },
        CalendarNotifications: function (organizationId: number): string {
            return Routes.LegFiCore.CalendarEvents(organizationId) + '/notify';
        },
        // OAuth Routes
        GoogleOAuth: function (organizationId: number): string {
            return Routes.LegFiCore.Organizations + '/' + organizationId + '/oauth/google/';
        },
        OrganizationOAuthSettings: function (organizationId: number): string {
            return Routes.LegFiCore.Organizations + '/' + organizationId + '/oauth/settings/';
        },
        GoogleOAuthSignout: function (organizationId: number): string {
            return Routes.LegFiCore.Organizations + '/' + organizationId + '/oauth/google/signout';
        },
        OAuthState: function (): string {
            return `/oauth/token/state/`;
        },
        GoogleCalendar: function (organizationId: number): string {
            return Routes.LegFiCore.Organizations + '/' + organizationId + '/oauth/google/calendar/';
        },
        GoogleCalendarList: function (organizationId: number): string {
            return Routes.LegFiCore.Organizations + '/' + organizationId + '/oauth/google/calendar/list';
        },
        LinkedFiles(subject: string, id: number): string {
            return `/files/${subject}/${id}`;
        },
        UploadFile() {
            return `/file/upload`;
        },
        GetFileUrls(id: number) {
            return `/file/url/${id}`;
        },
        CreateFileAssociation() {
            return `/file/link`;
        },
        SeverFileLink(id: number, linked_with_subject: string, linked_with_id: number): string {
            return `/file/${id}/${linked_with_subject}/${linked_with_id}`;
        },
        SeverFileLinks(ids: number[], linked_with_subject: string, linked_with_id: number): string {
            return `/file/${linked_with_subject}/${linked_with_id}?ids=` + ids.join(',');
        },

        Violations: '/violations',
        Violation: function (id: number): string {
            return Routes.LegFiCore.Violations + '/' + id;
        },
        ViolationStatus: function (id: number): string {
            return Routes.LegFiCore.Organization(id) + '/violation-status';
        },
        ViolationTemplates: function (id: number): string {
            return Routes.LegFiCore.Organization(id) + '/violation-templates';
        },
        ViolationHeaderFooter: function (id: number): string {
            return Routes.LegFiCore.Organization(id) + '/violation-header-footer';
        },
        OrganizationViolations: function (orgId: number, isExport: boolean = false): string {
            return Routes.LegFiCore.Organizations + '/' + orgId + '/violations' + (isExport ? '?export=1' : '');
        },
        OrganizationViolationsTable: function (orgId: number, isExport: boolean = false): string {
            return Routes.LegFiCore.Organizations + '/' + orgId + '/violations-table' + (isExport ? '?export=1' : '');
        },
        OrganizationViolationAccessors: function (orgId: number): string {
            return Routes.LegFiCore.Organizations + '/' + orgId + '/violations/accessors';
        },
        OrganizationViolationGraph: function (orgId: number): string {
            return Routes.LegFiCore.Organizations + '/' + orgId + '/violations/graphs';
        },
        OrgViolationPastDueCounts: function (orgId: number): string {
            return Routes.LegFiCore.Organizations + '/' + orgId + '/violations/past-due';
        },
        UnitViolations: function (orgId: number): string {
            return '/organizations/' + orgId + '/units/violations';
        },
        ViolationEmails: function (id: number): string {
            return Routes.LegFiCore.Organization(id) + '/violations/email';
        },
        ViolationResolution: function (id: number): string {
            return Routes.LegFiCore.Organization(id) + '/violations/resolution';
        },
        UnitViolationFines: function (orgId: number): string {
            return Routes.LegFiCore.Organizations + '/' + orgId + '/violations/fines';
        },
        ViolationComments: function (id: number): string {
            return Routes.LegFiCore.Violation(id) + '/comments';
        },

        /** Message board routes */
        MessageBoardBase: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/message-board';
        },
        MessageBoards: function (orgId: number): string {
            return Routes.LegFiCore.MessageBoardBase(orgId) + '/boards';
        },
        MessageBoard: function (orgId: number): string {
            return Routes.LegFiCore.MessageBoardBase(orgId) + `/board`;
        },
        MessageBoardDetail: function (orgId: number, boardId: number): string {
            return Routes.LegFiCore.MessageBoard(orgId) + `/${boardId}`;
        },
        MessageBoardDetailAction: function (orgId: number, boardId: number, action: string): string {
            return Routes.LegFiCore.MessageBoardDetail(orgId, boardId) + `/${action}`;
        },
        MessageBoardThread: function (orgId: number): string {
            return Routes.LegFiCore.MessageBoardBase(orgId) + `/thread`;
        },
        MessageBoardThreadDetail: function (orgId: number, threadId: number): string {
            return Routes.LegFiCore.MessageBoardThread(orgId) + `/${threadId}`;
        },
        MessageBoardThreadDetailAction: function (orgId: number, threadId: number, action: string): string {
            return Routes.LegFiCore.MessageBoardThreadDetail(orgId, threadId) + `/${action}`;
        },
        MessageBoardComment: function (orgId: number, commentId: number): string {
            return Routes.LegFiCore.MessageBoardBase(orgId) + `/comment/${commentId}`;
        },
        MessageBoardSubscribe: function (orgId: number): string {
            return Routes.LegFiCore.MessageBoardBase(orgId) + `/subscribe`;
        },
        MessageBoardSubscriptions: function (orgId: number): string {
            return Routes.LegFiCore.MessageBoardBase(orgId) + `/subscriptions`;
        },
        MessageBoardSubscription: function (orgId: number, subId: number): string {
            return Routes.LegFiCore.MessageBoardBase(orgId) + `/subscription/${subId}`;
        },

        /** Unit routes ish */
        Units: '/units',
        Unit: function (unitId: number): string {
            return Routes.LegFiCore.Units + '/' + unitId;
        },
        UnitAuxiliaryContacts: function (unitId: number): string {
            return Routes.LegFiCore.Unit(unitId) + '/auxiliary-contacts';
        },
        UnitCharges: function (orgId: number, unitId: number): string {
            return Routes.LegFiCore.OrganizationUnit(orgId, unitId) + '/charges';
        },
        UnitChargesRollup: function (orgId: number, unitId: number): string {
            return Routes.LegFiCore.OrganizationUnit(orgId, unitId) + '/charges/rollup';
        },
        UnitAccountHistory: function (orgId: number, unitId: number, currentOwner: boolean): string {
            return Routes.LegFiCore.Organization(orgId) + Routes.LegFiCore.Unit(unitId) + '/account-history/' + (currentOwner
                    ? 1
                    : 0);
        },
        OrganizationUnits: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) + '/units';
        },
        OrganizationUnitsSimple: function (orgId: number): string {
            return Routes.LegFiCore.OrganizationUnits(orgId) + '/simple';
        },
        OrganizationUnit: function (orgId: number, unitId: number): string {
            return Routes.LegFiCore.OrganizationUnits(orgId) + '/' + unitId;
        },
        OrganizationUnitBalance: function (orgId: number, unitId: number): string {
            return Routes.LegFiCore.OrganizationUnit(orgId, unitId) + '/balance';
        },
        OrganizationUnitOpeningBalance: function (orgId: number, unitId: number): string {
            return Routes.LegFiCore.OrganizationUnit(orgId, unitId) + '/opening-balance';
        },
        MemberUnits: function (orgId: number, memberId: number): string {
            return Routes.LegFiCore.Membership(orgId, memberId) + '/units';
        },
        OrganizationUnitImages: function (orgId: number, unitId: number): string {
            return Routes.LegFiCore.OrganizationUnit(orgId, unitId) + '/images';
        },
        OrganizationUnitImage: function (orgId: number, unitId: number, imageId: number): string {
            return Routes.LegFiCore.OrganizationUnitImages(orgId, unitId) + '/' + imageId;
        },
        OrganizationUnitImageCaption: function (orgId: number, unitId: number, imageId: number): string {
            return Routes.LegFiCore.OrganizationUnitImage(orgId, unitId, imageId) + '/caption';
        },
        ParseCsv: function (orgId): string {
            return Routes.LegFiCore.Organization(orgId) + '/csv/parse';
        },
        UnitsBulkImportFileCheck: function (orgId: number): string {
            return Routes.LegFiCore.UnitsBulkImport(orgId) + '/file-check';
        },
        UnitBulkImport: function (orgId: number): string {
            return Routes.LegFiCore.OrganizationUnits(orgId) + '/bulk-import';
        },
        UnitImportResults: function (orgId: number, batchId: string): string {
            return Routes.LegFiCore.UnitBulkImport(orgId) + '/' + batchId;
        },
        UnitMoveMembersIn: function (unitId: number): string {
            return Routes.LegFiCore.Unit(unitId) + '/owners/multiple';
        },
        UnitMoveMembersOut: function (unitId: number): string {
            return Routes.LegFiCore.Unit(unitId) + '/owners/moveOut';
        },
        UnitsBulkImport: function (orgId: number): string {
            return '/organizations/' + orgId + '/units/bulk-import';
        },
        UnitsBulkImportSupportedColumns: function (orgId: number): string {
            return Routes.LegFiCore.UnitsBulkImport(orgId) + '/columns';
        },
        UnitPdf: function (orgId: number, unitId: number, doIncludeAllOccupancies: boolean): string {
            return Routes.LegFiCore.OrganizationUnit(orgId, unitId) + '/' + (doIncludeAllOccupancies ? 1 : 0) + '/pdf';
        },
        RestoreUnit: function (): string {
            return `${Routes.LegFiCore.Units}/restore`;
        },

        Tags: '/tags',
        Tag: function (tagId: number): string {
            return Routes.LegFiCore.Tags + '/' + tagId;
        },
        OrganizationTags: function (organizationId: number): string {
            return Routes.LegFiCore.Organization(organizationId) + '/tags';
        },
        OrganizationTagsByType: function (organizationId: number, type: string): string {
            return Routes.LegFiCore.OrganizationTags(organizationId) + '/' + type;
        },
        OrganizationTagsByTypeAndModel: function (organizationId: number, type: string, modelIds: number[]): string {
            return Routes.LegFiCore.OrganizationTagsByType(organizationId, type) + '?ids=' + modelIds.join(',');
        },
        ManageOrganizationTags: function (organizationId: number): string {
            return Routes.LegFiCore.Organization(organizationId) + '/tags/manage';
        },
        ManageOrganizationTagsByType: function (organizationId: number, type: string): string {
            return Routes.LegFiCore.ManageOrganizationTags(organizationId) + '/' + type;
        },

        // Forms Simplified
        Forms: '/forms',
        FormCounts: '/form-counts',
        Form: function (formId: number): string {
            return `${Routes.LegFiCore.Forms}/${formId}`;
        },
        FormSubmissions: function (formId: number): string {
            return `${Routes.LegFiCore.Forms}/${formId}/submissions`;
        },
        FormApprovers: function (formId: number): string {
            return `${Routes.LegFiCore.Forms}/${formId}/approvers`;
        },
        FormPermissions: function (formId: number): string {
            return `${Routes.LegFiCore.Forms}/${formId}/permissions`;
        },
        FormSubmissionComments: function (formSubmissionId: number): string {
            return `/form-submissions/${formSubmissionId}/comments`;
        },

        Form1099s: '/form-1099s',
        PageAdvertisement: '/page-advertisement',

        SearchKeyGlobal: '/search/key/global',
        SearchKeyOrganizationUnits: '/search/key/organization/units',

        // Website Builder routes
        Website: '/website-builder/website',
        WebsiteById: function (id: number) {
            return `/website-builder/website/${id}`;
        },
        WebsitePublish: function (id: number) {
            return `/website-builder/website/${id}/publish`;
        },
        WebsitePreview: function (id: number) {
            return `/website-builder/website/${id}/preview`;
        },
        CheckSubdomain: function () {
            return `/website-builder/check-subdomain`;
        },
        WebsiteBuilderUploadImage: '/website-builder/upload-image',
    };

    public static AmenityModule = {
        // Helper to construct base Amenity module organization path with module prefix
        _baseOrgPath: function (orgId: number): string {
            return '/amenity/v1/organizations/' + orgId;
        },
        OrgAmenities: function (orgId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities';
        },
        OrgAmenity: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId;
        },
        OrgAmenityRestore: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/restore';
        },
        OrgAmenityForceDelete: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/force-delete';
        },
        OrgAmenityAvailability: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/availability';
        },
        OrgAmenityAvailabilitySchedules: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/availability-schedules';
        },
        OrgAmenityAvailableSpaces: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/available-spaces';
        },
        OrgAmenityCategories: function (orgId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-categories';
        },
        OrgAmenityCategory: function (orgId: number, categoryId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-categories/' + categoryId;
        },
        OrgAmenityFeatures: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/amenity-features';
        },
        OrgAmenityFeature: function (orgId: number, amenityId: number, featureId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/amenity-features/' + featureId;
        },
        OrgAmenityRules: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/amenity-rules';
        },
        OrgAmenityRule: function (orgId: number, amenityId: number, ruleId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/amenity-rules/' + ruleId;
        },
        OrgAmenityAgreementDocuments: function (orgId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-agreement-documents';
        },
        OrgAmenityAgreementDocument: function (orgId: number, documentId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-agreement-documents/' + documentId;
        },
        OrgAmenityAgreementDocumentDownload: function (orgId: number, documentId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-agreement-documents/' + documentId + '/download';
        },
        OrgAmenityAgreementDocumentsBulkLink: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/agreement-documents/bulk-link';
        },
        OrgAmenityImages: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/amenity-images';
        },
        OrgAmenityImage: function (orgId: number, amenityId: number, imageId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/amenity-images/' + imageId;
        },
        OrgAmenitySpaces: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/spaces';
        },
        OrgAmenitySpace: function (orgId: number, amenityId: number, spaceId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/spaces/' + spaceId;
        },
        OrgAmenityReviews: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/amenity-reviews';
        },
        OrgAmenityReview: function (orgId: number, amenityId: number, reviewId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/amenity-reviews/' + reviewId;
        },
        OrgAmenityBookings: function (orgId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-bookings';
        },
        OrgAmenityBooking: function (orgId: number, bookingId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-bookings/' + bookingId;
        },
        OrgAmenityBookingCancel: function (orgId: number, bookingId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-bookings/' + bookingId + '/cancel';
        },
        OrgAmenityBookingReschedule: function (orgId: number, bookingId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-bookings/' + bookingId + '/reschedule';
        },
        // Amenity booking approval endpoints (by approval ID - requires approval record)
        OrgAmenityBookingApprovalApprove: function (orgId: number, approvalId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-booking-approvals/' + approvalId + '/approve';
        },
        OrgAmenityBookingApprovalDeny: function (orgId: number, approvalId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-booking-approvals/' + approvalId + '/deny';
        },
        // Booking-based approve/deny (works even without approval record - "any admin can approve" workflow)
        OrgAmenityBookingApprove: function (orgId: number, bookingId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-bookings/' + bookingId + '/approve';
        },
        OrgAmenityBookingDeny: function (orgId: number, bookingId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-bookings/' + bookingId + '/deny';
        },
        // Amenity statistics/widgets
        OrgAmenityStatsPendingApprovals: function (orgId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/stats/pending-approvals';
        },
        OrgAmenityStatsUpcomingBookings: function (orgId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/stats/upcoming-bookings';
        },
        OrgAmenityStatsTotalAmenities: function (orgId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/stats/total-amenities';
        },
        OrgAmenityStatsRevenueThisYear: function (orgId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/stats/revenue-this-year';
        },
        // Homeowner-facing amenity endpoints
        OrgHomeownerAmenities: function (orgId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/homeowner/amenities';
        },
        OrgHomeownerAmenity: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/homeowner/amenities/' + amenityId;
        },
        OrgHomeownerAmenityAvailability: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/homeowner/amenities/' + amenityId + '/availability';
        },
        OrgHomeownerAmenityAvailableSpaces: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/homeowner/amenities/' + amenityId + '/available-spaces';
        },
        OrgHomeownerBookings: function (orgId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/homeowner/bookings';
        },
        OrgHomeownerBooking: function (orgId: number, bookingId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/homeowner/bookings/' + bookingId;
        },
        OrgHomeownerBookingCancel: function (orgId: number, bookingId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/homeowner/bookings/' + bookingId + '/cancel';
        },
        OrgHomeownerBookingReschedule: function (orgId: number, bookingId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/homeowner/bookings/' + bookingId + '/reschedule';
        },
        OrgHomeownerBookingConfirm: function (orgId: number, bookingId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/homeowner/bookings/' + bookingId + '/confirm';
        },
        // Special hours endpoints
        OrgAmenitySpecialHours: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/special-hours';
        },
        OrgAmenitySpecialHour: function (orgId: number, amenityId: number, specialHoursId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/special-hours/' + specialHoursId;
        },
        // Share booking endpoints
        OrgAmenityShareRecipients: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/share-recipients';
        },
        ShareAmenityBooking: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/share-booking';
        },
        // Share booking draft endpoints
        ShareAmenityBookingDraft: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/share-booking/draft';
        },
        // Timeline endpoints
        OrgAmenityTimeline: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/timeline';
        },
        OrgAmenityBookingTimeline: function (orgId: number, bookingId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenity-bookings/' + bookingId + '/timeline';
        },
        // Scheduled share booking endpoints
        OrgAmenityScheduledShares: function (orgId: number, amenityId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/scheduled-shares';
        },
        OrgAmenityScheduledShare: function (orgId: number, amenityId: number, scheduledShareId: number): string {
            return Routes.AmenityModule._baseOrgPath(orgId) + '/amenities/' + amenityId + '/scheduled-shares/' + scheduledShareId;
        },
    };

    public static BookkeepingModule = {
        // Helper to construct base Bookkeeping module organization path with module prefix
        _baseOrgPath: function (orgId: number): string {
            return '/bookkeeping/v1/organizations/' + orgId;
        },
        // Enrollment endpoints (singular - one enrollment per organization)
        OrgEnrollment: function (orgId: number, enrollmentId?: number): string {
            if (enrollmentId) {
                return Routes.BookkeepingModule._baseOrgPath(orgId) + '/enrollment/' + enrollmentId;
            }
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/enrollment';
        },
        OrgEnrollmentSubmit: function (orgId: number, enrollmentId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/enrollment/' + enrollmentId + '/submit';
        },
        OrgEnrollmentDeleteFile: function (orgId: number, enrollmentId: number, uploadedFileId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/enrollment/' + enrollmentId + '/files/' + uploadedFileId;
        },
        // Dashboard endpoint
        Dashboard: function (orgId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/dashboard';
        },
        // Default representative info for enrollment pre-population
        DefaultRepresentative: function (orgId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/dashboard/default-representative';
        },
        // Bank statement endpoints
        BankStatements: function (orgId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/bank-statements';
        },
        BankStatement: function (orgId: number, bankStatementId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/bank-statements/' + bankStatementId;
        },
        BankStatementUpload: function (orgId: number, bankStatementId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/bank-statements/' + bankStatementId + '/upload';
        },
        BankAccountStatementUpload: function (orgId: number, bankAccountId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/bank-accounts/' + bankAccountId + '/upload-statement';
        },
        BankStatementDeleteFile: function (orgId: number, bankStatementId: number, uploadedFileId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/bank-statements/' + bankStatementId + '/files/' + uploadedFileId;
        },
        BankStatementDownloadFile: function (orgId: number, bankStatementId: number, uploadedFileId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/bank-statements/' + bankStatementId + '/files/' + uploadedFileId;
        },
        ReportPacketDownloadFile: function (orgId: number, reportPacketId: number, uploadedFileId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/report-packets/' + reportPacketId + '/files/' + uploadedFileId;
        },
        ReportPacketFromSavedReport: function (orgId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/report-packets/from-saved-report';
        },
        DismissAabBanner: function (orgId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/dashboard/dismiss-aab-banner';
        },
        // Pricing calculator endpoint
        CalculatePricing: function (orgId: number): string {
            return Routes.BookkeepingModule._baseOrgPath(orgId) + '/pricing/calculate';
        },

        // =====================
        // Base path for module
        // =====================
        _basePath: function (): string {
            return '/bookkeeping/v1';
        },
        // =====================
        // Admin endpoints (Super Admin / Bookkeeper Admin)
        // =====================
        _baseAdminPath: function (): string {
            return Routes.BookkeepingModule._basePath() + '/admin';
        },
        // Dashboard stats
        AdminDashboard: function (): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/dashboard';
        },
        // Enrollments list and detail
        AdminEnrollments: function (): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments';
        },
        AdminEnrollment: function (enrollmentId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId;
        },
        // Enrollment actions
        AdminEnrollmentCancel: function (enrollmentId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/cancel';
        },
        AdminEnrollmentRetire: function (enrollmentId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/retire';
        },
        // Bookkeepers CRUD (not under /admin)
        AdminBookkeepers: function (): string {
            return Routes.BookkeepingModule._basePath() + '/bookkeepers';
        },
        AdminBookkeepersAvailableUsers: function (): string {
            return Routes.BookkeepingModule._basePath() + '/bookkeepers/available-users';
        },
        AdminBookkeepersCreateWithUser: function (): string {
            return Routes.BookkeepingModule._basePath() + '/bookkeepers/create-with-user';
        },
        AdminBookkeeper: function (bookkeeperId: number): string {
            return Routes.BookkeepingModule._basePath() + '/bookkeepers/' + bookkeeperId;
        },
        // Current bookkeeper profile endpoint
        AdminBookkeeperProfile: function (): string {
            return Routes.BookkeepingModule._basePath() + '/bookkeeper/profile';
        },
        // Bookkeeper Assignments CRUD (not under /admin)
        AdminAssignments: function (): string {
            return Routes.BookkeepingModule._basePath() + '/bookkeeper-assignments';
        },
        AdminAssignment: function (assignmentId: number): string {
            return Routes.BookkeepingModule._basePath() + '/bookkeeper-assignments/' + assignmentId;
        },
        // Onboarding Checklists (generic CRUD)
        AdminOnboardingChecklists: function (): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/onboarding-checklists';
        },
        AdminOnboardingChecklist: function (checklistId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/onboarding-checklists/' + checklistId;
        },
        AdminOnboardingChecklistItems: function (checklistId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/onboarding-checklists/' + checklistId + '/items';
        },
        AdminOnboardingChecklistItem: function (checklistId: number, itemId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/onboarding-checklists/' + checklistId + '/items/' + itemId;
        },
        // Enrollment-specific Checklists
        AdminMasterChecklists: function (): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/master-checklists';
        },
        AdminEnrollmentOnboardingChecklists: function (enrollmentId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/onboarding-checklists';
        },
        AdminEnrollmentChecklistItemComplete: function (enrollmentId: number, bookkeepingChecklistId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/checklist-items/' + bookkeepingChecklistId + '/complete';
        },
        AdminEnrollmentChecklistItemUncomplete: function (enrollmentId: number, bookkeepingChecklistId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/checklist-items/' + bookkeepingChecklistId;
        },
        // Organization-specific settings (admin only)
        AdminOrganizationSettings: function (organizationId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/organizations/' + organizationId + '/settings';
        },
        // Global settings (admin only)
        AdminSettings: function (): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/settings';
        },
        // Internal Notes
        AdminEnrollmentInternalNotes: function (enrollmentId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/internal-notes';
        },
        AdminEnrollmentInternalNote: function (enrollmentId: number, noteId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/internal-notes/' + noteId;
        },
        // Enrollment Timeline
        AdminEnrollmentTimeline: function (enrollmentId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/timeline';
        },
        // Report Packets (admin delete)
        AdminReportPacketDelete: function (enrollmentId: number, reportPacketId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/report-packets/' + reportPacketId;
        },
        // Enrollment Attachments (admin delete own uploads)
        AdminEnrollmentAttachment: function (enrollmentId: number, fileId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/attachments/' + fileId;
        },
        // Enrollment Attachment Download (admin)
        AdminEnrollmentAttachmentDownload: function (enrollmentId: number, fileId: number): string {
            return Routes.BookkeepingModule._baseAdminPath() + '/enrollments/' + enrollmentId + '/attachments/' + fileId + '/download';
        },

        // =====================
        // BOOKKEEPER DASHBOARD (Personal bookkeeper view - not admin)
        // =====================
        _baseBookkeeperPath: function (): string {
            return Routes.BookkeepingModule._basePath() + '/bookkeeper';
        },
        // Bookkeeper's personal dashboard stats
        BookkeeperDashboard: function (): string {
            return Routes.BookkeepingModule._baseBookkeeperPath() + '/dashboard';
        },
        // Bookkeeper's assigned clients list
        BookkeeperClients: function (): string {
            return Routes.BookkeepingModule._baseBookkeeperPath() + '/clients';
        },
        // Single client detail for bookkeeper
        BookkeeperClient: function (enrollmentId: number): string {
            return Routes.BookkeepingModule._baseBookkeeperPath() + '/clients/' + enrollmentId;
        },
        // Upload report packet for a client (bookkeeper context)
        BookkeeperClientUploadReport: function (enrollmentId: number): string {
            return Routes.BookkeepingModule._baseBookkeeperPath() + '/clients/' + enrollmentId + '/report-packets';
        },
        // Bank statements for a client (bookkeeper context)
        BookkeeperClientBankStatements: function (enrollmentId: number): string {
            return Routes.BookkeepingModule._baseBookkeeperPath() + '/clients/' + enrollmentId + '/bank-statements';
        },
        // Download bank statement attachment (bookkeeper context)
        BookkeeperClientBankStatementDownload: function (enrollmentId: number, bankStatementId: number, uploadedFileId: number): string {
            return Routes.BookkeepingModule._baseBookkeeperPath() + '/clients/' + enrollmentId + '/bank-statements/' + bankStatementId + '/files/' + uploadedFileId;
        },
        // Report packets for a client (bookkeeper context)
        BookkeeperClientReportPackets: function (enrollmentId: number): string {
            return Routes.BookkeepingModule._baseBookkeeperPath() + '/clients/' + enrollmentId + '/report-packets';
        },
        // Download report packet attachment (bookkeeper context)
        BookkeeperClientReportPacketDownload: function (enrollmentId: number, reportPacketId: number, uploadedFileId: number): string {
            return Routes.BookkeepingModule._baseBookkeeperPath() + '/clients/' + enrollmentId + '/report-packets/' + reportPacketId + '/files/' + uploadedFileId;
        },
    };

    public static VotingModule = {
        OrgBallotAnswers: function (
            orgId: number,
            ballotQuestionId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-questions/' +
                ballotQuestionId +
                '/ballot-answers';
        },
        OrgBallotAnswersReorder: function (
            orgId: number,
            ballotQuestionId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-questions/' +
                ballotQuestionId +
                '/ballot-answers/reorder';
        },
        OrgBallotAnswer: function (
            orgId: number,
            ballotAnswerId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-answers/' +
                ballotAnswerId;
        },
        OrgBallotQuestionAnswer: function (
            orgId: number,
            ballotQuestionId: number,
            ballotAnswerId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-questions/' +
                ballotQuestionId +
                '/ballot-answers/' +
                ballotAnswerId;
        },
        OrgBallotBallotQuestion: function (
            orgId: number,
            ballotId: number,
            ballotQuestionId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/ballot-questions/' +
                ballotQuestionId;
        },
        OrgBallotQuestionDuplicate: function (
            orgId: number,
            ballotQuestionId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-questions/' +
                ballotQuestionId +
                '/duplicate';
        },
        OrgShowBallotQuestion: function (
            orgId: number,
            ballotQuestionId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-questions/' +
                ballotQuestionId;
        },
        OrgBallotQuestionAttachment: function (
            orgId: number,
            ballotQuestionId: number,
            attachmentId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-questions/' +
                ballotQuestionId +
                '/attachments/' +
                attachmentId;
        },
        OrgBallotQuestions: function (
            orgId: number,
            ballotId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/ballot-questions';
        },
        OrgBallotReorderQuestions: function (
            orgId: number,
            ballotId: number
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/reorder-questions';
        },
        OrgBallotQuestionTypes: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-question-types';
        },
        OrgBallotQuestionType: function (
            orgId: number,
            ballotQuestionTypeId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-question-types/' +
                ballotQuestionTypeId;
        },
        OrgMembershipSearch: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/search-ballot-members';
        },
        OrgMembershipProxySearch: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/search-ballot-proxies';
        },
        OrgBallot: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId;
        },
        OrgBallotStatus: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/status';
        },
        OrgBallotDuplicate: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/duplicate';
        },
        OrgBallotDuplicationStatus: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/duplication-status';
        },
        OrgBallotWithMember: function (
            orgId: number,
            ballotId: number,
            membershipId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/member/' +
                membershipId;
        },
        OrgBallotSendPaperBallots: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/send-paper-ballots';
        },
        OrgBallotShare: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/share-results';
        },
        OrgBallotShareWithMyself: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/share-results-with-myself';
        },
        OrgResendOnlineBallots: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/resend-online-ballots';
        },
        OrgResendOnlineBallotToMyself: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/resend-online-ballot-to-myself';
        },
        OrgBallots: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots';
        },
        OrgBallotsPaginated: function (orgId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots-paginated';
        },
        OrgBallotAttachment: function (orgId: number, ballotId: number, attachmentId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/attachments/' +
                attachmentId;
        },
        OrgBallotSummary: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/summary';
        },
        OrgBallotPreview: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/preview-pdf';
        },
        OrgBallotMembers: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/ballot-members';
        },
        OrgBallotMember: function (
            orgId: number,
            ballotId: number,
            ballotMemberId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/ballot-members/' +
                ballotMemberId;
        },
        OrgBallotMembership: function (
            orgId: number,
            ballotId: number,
            membershipId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/memberships/' +
                membershipId;
        },
        OrgBallotMemberProxy: function (
            orgId: number,
            ballotId: number,
            ballotMemberId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/ballot-members/' +
                ballotMemberId +
                '/update-proxy';
        },
        OrgBallotMemberResetResponses: function (
            orgId: number,
            ballotId: number,
            ballotMemberId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballots/' +
                ballotId +
                '/ballot-members/' +
                ballotMemberId +
                '/reset-responses';
        },
        OrgBallotMemberResponses: function (
            orgId: number,
            ballotMemberId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-members/' +
                ballotMemberId +
                '/ballot-member-responses';
        },
        OrgBallotMemberResponse: function (
            orgId: number,
            ballotMemberId: number,
            ballotMemberResponseId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-members/' +
                ballotMemberId +
                '/ballot-member-responses/' +
                ballotMemberResponseId;
        },
        OrgBallotVotingSettingsTemplates: function (
            orgId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-voting-settings-templates';
        },
        OrgBallotVotingSettingsTemplatesPaginated: function (
            orgId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-voting-settings-templates/paginated';
        },
        OrgBallotVotingSettingsTemplate: function (
            orgId: number,
            ballotVotingSettingsTemplateId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-voting-settings-templates/' +
                ballotVotingSettingsTemplateId;
        },
        OrgWeightedVotingCategories: function (
            orgId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-weighted-voting-categories';
        },
        OrgWeightedVotingCategoriesPaginated: function (
            orgId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-weighted-voting-categories/paginated';
        },
        OrgWeightedVotingCategory: function (
            orgId: number,
            weightedVotingCategoryId: number,
        ): string {
            return Routes.LegFiCore.Organization(orgId) +
                '/ballot-weighted-voting-categories/' +
                weightedVotingCategoryId;
        },
        BallotProxy: function (
            ballotId: number,
            ballotProxyId: number,
        ): string {
            return '/ballots/' +
                ballotId +
                '/ballot-proxies/' +
                ballotProxyId;
        },
        BallotProxies: function (
            ballotId: number
        ): string {
            return '/ballots/' +
                ballotId +
                '/ballot-proxies';
        },

        // User Voting Endpoints

        VoterUserOrgBallotAttachment: function (orgId: number, ballotId: number, attachmentId: number): string {
            return Routes.LegFiCore.VoterUserOrganization(orgId) +
                '/ballots/' +
                ballotId +
                '/attachments/' +
                attachmentId;
        },
        VoterUserOrgBallotMemberProxy: function (
            orgId: number,
            ballotId: number,
            ballotMemberId: number,
        ): string {
            return Routes.LegFiCore.VoterUserOrganization(orgId) +
                '/ballots/' +
                ballotId +
                '/ballot-members/' +
                ballotMemberId +
                '/update-proxy';
        },
        VoterUserOrgBallotMemberResponses: function (
            orgId: number,
            ballotMemberId: number,
        ): string {
            return Routes.LegFiCore.VoterUserOrganization(orgId) +
                '/ballot-members/' +
                ballotMemberId +
                '/ballot-member-responses';
        },
        VoterUserOrgBallotQuestionAttachment: function (
            orgId: number,
            ballotQuestionId: number,
            attachmentId: number
        ): string {
            return Routes.LegFiCore.VoterUserOrganization(orgId) +
                '/ballot-questions/' +
                ballotQuestionId +
                '/attachments/' +
                attachmentId;
        },
        VoterUserOrgBallotsPaginated: function (orgId: number): string {
            return Routes.LegFiCore.VoterUserOrganization(orgId) +
                '/ballots-paginated';
        },
        VoterUserOrgBallots: function (orgId: number): string {
            return Routes.LegFiCore.VoterUserOrganization(orgId) +
                '/ballots';
        },
        VoterUserOrgBallot: function (orgId: number, ballotId: number): string {
            return Routes.LegFiCore.VoterUserOrganization(orgId) +
                '/ballots/' +
                ballotId;
        },
        VoterUserOrgBallotMembership: function (
            orgId: number,
            ballotId: number,
            membershipId: number,
        ): string {
            return Routes.LegFiCore.VoterUserOrganization(orgId) +
                '/ballots/' +
                ballotId +
                '/memberships/' +
                membershipId;
        },
    };

    public static WebsiteBuilderModule = {
        Website: () => {
            return ``;
        },
        WebsiteById: (id: number) => {
            return `/${id}`;
        },
        PublishWebsite: (id: number) => {
            return `/${id}/publish`;
        },
        PreviewWebsite: (id: number) => {
            return `/${id}/preview`;
        },
        CheckSubdomain: () => {
            return `/check-subdomain`;
        },
        LinkImage: () => {
            return `/link-image`;
        }
    };
    public static InboxModule = {
        // Inbox Module API endpoints - matches backend Inbox module routes
        Items: () => {
            return `/ai-chat/conversations`;
        },
        Stats: () => {
            return `/ai-chat/stats`;
        },
        Channels: () => {
            return `/ai-chat/channels`;
        },
        MarkAsRead: (channelType: string, sourceId: string) => {
            return `/${channelType}/${sourceId}/read`;
        },
        MarkAsUnread: (channelType: string, sourceId: string) => {
            return `/${channelType}/${sourceId}/unread`;
        },
        UpdateStatus: (channelType: string, sourceId: string) => {
            return `/${channelType}/${sourceId}/status`;
        },
        Archive: (channelType: string, sourceId: string) => {
            return `/${channelType}/${sourceId}/archive`;
        },
        Unarchive: (channelType: string, sourceId: string) => {
            return `/${channelType}/${sourceId}/unarchive`;
        },
        AIChatConversation: (conversationId: string) => {
            return `/ai-chat/conversations/${conversationId}`;
        },
        ReplyToAIChat: (conversationId: string) => {
            return `/ai-chat/conversations/${conversationId}/reply`;
        }
    };

    public static MakeAmenityModuleUrl(path: string): string {
        return Routes.MakeLegFiCoreUrl('/amenity' + path);
    }

    public static MakeBookkeepingModuleUrl(path: string): string {
        return Routes.MakeLegFiCoreUrl('/bookkeeping' + path);
    }

    public static MakeVotingModuleUrl(path: string): string {
        return Routes.MakeLegFiCoreUrl('/voting' + path);
    }

    public static MakeInboxModuleUrl(path: string, organizationId?: number): string {
        // FIXED: InboxModule routes need /api/ prefix since they're registered under API middleware
        if (organizationId) {
            return Routes.MakeLegFiCoreUrl(`/api/organizations/${organizationId}/inbox${path}`);
        }
        // Fallback without organization (shouldn't be used, but keeping for safety)
        return Routes.MakeLegFiCoreUrl('/api/inbox' + path);
    }

    public static MakeWebsiteBuilderModuleUrl(path: string, organizationId?: number): string {
        if (organizationId) {
            return Routes.MakeLegFiCoreUrl(`/organizations/${organizationId}/website-builder${path}`);
        }
        return Routes.MakeLegFiCoreUrl('/website-builder' + path);
    }

    public static BugReportModule = {
        Submit: (orgId: number): string => {
            return Routes.MakeLegFiCoreUrl('/core/v2/organizations/' + orgId + '/bug-reports');
        },
        Activity: (): string => {
            return Routes.MakeLegFiCoreUrl('/core/v2/bug-reports/activity');
        },
        List: (): string => {
            return Routes.MakeLegFiCoreUrl('/core/v2/bug-reports');
        },
        Detail: (id: number): string => {
            return Routes.MakeLegFiCoreUrl('/core/v2/bug-reports/' + id);
        },
    };

    public static MakeAITransactionsModuleUrl(orgId: number, path: string): string {
        return Routes.MakeLegFiCoreUrl('/ai-transactions/v1/organizations/' + orgId + path);
    }

    /**
     * @param path
     * @returns {string}
     */
    public static MakeLegFiCoreUrl(path: string) {
        return environment.LegFiCoreApi + path;
    }

    /**
     * @param {number} siteId
     * @returns {string}
     */
    public static GetUiDomain(siteId: number) {
        return environment.PayHoaUi;
    }
}
